/**
 * MessageComposer — create/edit a channel-complete message. Verbatim port of
 * the prototype's MessageComposer: channel toggles, per-channel content tabs,
 * live per-channel preview, autosave.
 *
 * Route: `messaging.compose` (params: new | id | view | channel | auto).
 *
 * TWO MODES BEHIND ONE ROUTE (2026-09-16, CD docket "a separate editable SMS
 * template for every automatic message"). `auto=1&channel=sms` opens
 * `AutomaticSmsComposer` — the same composer shell, on the SMS content of ONE
 * automatic message; anything else opens the library composer below, unchanged.
 *
 * Why a mode rather than the library composer with the SMS tab preselected: the
 * library composer is a WHOLE-ROW editor. It autosaves the entire form through
 * `store.upsert` — name, channels, subject, every channel body, lifecycle
 * commands — against the message-library cache, which does not contain automatic
 * rows at all (its read is the unscoped library list). Pointing it at
 * `mast_appointment_reminder_60` would seed a blank draft and then write the
 * whole row back, which is exactly what the docket forbids: no whole-row echo,
 * no email content touched, no `enabled` bundled with a text edit, no other
 * reminder offset affected. The mode writes `{ perChannel: { sms } }` and
 * nothing else, and shows no draft/publish chrome for a lineage that has none.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { alpha, Btn, IconBtn, Icon, Card, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { useAutosave, AutosaveBadge } from "../../shell/editor-hooks";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import { ChGlyph, MSG_CHANNEL, BUILDER_CHANNELS, CONTENT_CHANNELS, STUB_BUILDER_CHANNELS, msgStatusPill, triggerReading } from "./messaging-shared";
import { useMessaging } from "./useMessaging";
import { useFlows } from "./useFlows";
import { flowStats, FLOW_KIND_PILL } from "./flows-seed";
import { fbMkComms, fbInsert, fbWaitLabel, type FBStep } from "../builder/fb-model";
import { useTicketTypes, TT_KINDS } from "../tickets/ticket-types-store";
import { WA_DEFAULT } from "./messaging-seed";
import type { MessageItem, MessageTranslation, WaTemplate, WaCategory, WaHeaderType, WaButtonType, WaButton, WaStatus } from "./messaging-seed";
import { useT, t as i18nT, availableLocales, localeDisplayName } from "../../lib/i18n";
import { emailHasContent, type EmailDesign } from "./email/email-model";
import { renderEmailHtml } from "./email/email-render";
import { renderMessage, type MsgRender } from "./message-render-api";
import { TokenField, contextOf, smsSegments, compileForWhatsApp, resolveSamples, hasTokens, tokenDef } from "./tokens";
import { SMS_MAX, findSmsRow, smsTextOf, smsTokenContext, outOfScopeTokens, saveSmsText, SmsNotPersistedError } from "./sms-template";
import type { StudioMessage } from "@v0y4ge/api-contracts";
import { timingLine, purposeLine, toMessageRow } from "./AutomaticEmails";
import { analyzeSms, allFixable, describeChar, toGsmSafe } from "./sms-encoding";
import { ICS_DEFAULT, ICS_METHODS, buildIcs, type IcsTemplate, type IcsMethod } from "./calendar-ics";
import { WALLET_DEFAULTS, WALLET_TYPE_LABEL, WALLET_UPDATE_TRIGGER, walletTypeFor, walletEligibleFor, type WalletTemplate, type WalletField, type WalletPassType } from "./wallet-pass";
import { artefactsOf, ARTEFACT_MODES, ARTEFACT_MODE_LABEL, ARTEFACT_MODE_HINT, type ArtefactId, type ArtefactMode } from "./artefacts";
import { useBranding } from "../settings/useBranding";
import { useCollections } from "../../lib/collections-api";

const CARET = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
);

const WA_HUE = "#25D366";
const WA_CATS: { id: WaCategory; label: string; labelKey: string; sub: string }[] = [
  { id: "marketing", label: "Marketing", labelKey: "messaging.messageComposer.marketing", sub: "Invites, promos, announcements" },
  { id: "utility", label: "Utility", labelKey: "messaging.messageComposer.utility", sub: "Confirmations, reminders, updates" },
  { id: "authentication", label: "Authentication", labelKey: "messaging.messageComposer.authentication", sub: "One-time passcodes" },
];
const WA_HEADERS: { id: WaHeaderType; label: string; labelKey: string }[] = [
  { id: "none", label: "None", labelKey: "messaging.messageComposer.none" }, { id: "text", label: "Text", labelKey: "messaging.messageComposer.text" }, { id: "image", label: "Image", labelKey: "messaging.messageComposer.image" }, { id: "video", label: "Video", labelKey: "messaging.messageComposer.video" }, { id: "document", label: "Document", labelKey: "messaging.messageComposer.document" },
];
const WA_BTN_TYPES: { id: WaButtonType; label: string; labelKey: string }[] = [
  { id: "quick_reply", label: "Quick reply", labelKey: "messaging.messageComposer.quickReply" }, { id: "url", label: "Visit URL", labelKey: "messaging.messageComposer.visitUrl" }, { id: "call", label: "Call", labelKey: "messaging.messageComposer.call" },
];
const WA_STATUS_PILL: Record<WaStatus, { tone: "success" | "info" | "warn" | "neutral"; label: string; labelKey: string }> = {
  draft: { tone: "neutral", label: "Draft", labelKey: "common.status.draft" },
  in_review: { tone: "info", label: "In review", labelKey: "messaging.messageComposer.inReview" },
  active: { tone: "success", label: "Active", labelKey: "common.status.active" },
  rejected: { tone: "warn", label: "Rejected", labelKey: "messaging.messageComposer.rejected" },
};
let _btnSeq = 0;
const waBtnId = () => `wab_${Date.now().toString(36)}${(_btnSeq++).toString(36)}`;

// Consent-based channels can only reach opted-in / recently-engaged contacts —
// they are not broadcast to a ticket segment the way email / SMS are. TikTok,
// Instagram and Facebook were retired from the builder 2026-07-30 (operator:
// replacements come later), leaving WhatsApp as the one consent channel.
const CONSENT_CHANNELS = ["whatsapp"];

const msgLabel = { fontSize: 13, fontWeight: 600, color: T.n1, marginBottom: 6 } as const;
/** Required-field marker (2026-07-31, operator). Asterisk on the label, plus an
 *  inline message the moment the field is emptied — the Make-Live gate stays as
 *  the backstop rather than being the only feedback. */
const Req = () => <span aria-hidden="true" style={{ color: T.red1, marginLeft: 3 }}>*</span>;
const FieldError = ({ show, children }: { show: boolean; children: React.ReactNode }) =>
  show ? <div role="alert" style={{ fontSize: 11.5, color: T.red1, marginTop: 5, display: "flex", alignItems: "center", gap: 5 }}><Icon.warn size={12} />{children}</div> : null;
const msgInput = (extra: React.CSSProperties = {}): React.CSSProperties => ({ width: "100%", height: 40, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8, background: T.white, color: T.n1, fontSize: 14, fontFamily: T.font, outline: "none", boxSizing: "border-box", ...extra });

export default function MessageComposer() {
  const nav = useNav();
  const id = typeof nav.params.id === "string" ? nav.params.id : null;
  /* An EXPLICIT marker, not a guess from the channel param: "edit this automatic
     message's SMS content" is a different job from "open this library message
     with SMS showing", and inferring it would silently narrow a library message
     someone linked with ?channel=sms. */
  if (id && !nav.params.new && nav.params.auto && nav.params.channel === "sms") {
    return <AutomaticSmsComposer id={id} />;
  }
  return <LibraryComposer />;
}

function LibraryComposer() {
  const t = useT();
  const nav = useNav();
  const store = useMessaging();
  const ticketTypes = useTicketTypes();
  const kindsPresent = TT_KINDS.filter((k) => ticketTypes.some((t) => t.kind === k.id));
  const isNew = !!(nav.params && nav.params.new);
  // Role gates — Creator/Manager/Admin author (campaigns.write); Coordinator/
  // Manager/Admin send (campaigns.send). No write ⇒ forced read-only preview.
  const canWrite = can("campaigns.write");
  const viewOnly = !!(nav.params && nav.params.view) || !canWrite;
  const id = nav.params && (nav.params.id as string | undefined);
  const seed = (): MessageItem => isNew
    ? store.newMessage()
    : (id && store.get(id) ? JSON.parse(JSON.stringify(store.get(id))) : store.newMessage());
  const [form, setForm] = useState<MessageItem>(seed);
  const routeKey = `${isNew ? "new" : id || ""}:${viewOnly ? "v" : "e"}:${store.loading ? "l" : "r"}`;
  useEffect(() => { setForm(seed()); }, [routeKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Channels the builder offers (retired ones filtered). A legacy message that
  // still carries tiktok/instagram/facebook content keeps that data untouched —
  // those tabs are simply no longer shown or editable here.
  const activeChannels = form.channels.filter((c) => BUILDER_CHANNELS.some((b) => b.id === c));
  const [active, setActive] = useState<string>(activeChannels[0] || "email");
  useEffect(() => { if (!activeChannels.includes(active)) setActive(activeChannels[0] || "email"); }, [activeChannels.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Languages (REQ-112). Root fields hold the DEFAULT language; every other
     language lives in form.translations[tag]. Legacy rows = single "en". ── */
  const defLang = form.defaultLanguage || "en";
  const langs = form.languages && form.languages.length ? form.languages : [defLang];
  const [lang, setLang] = useState<string>(defLang);
  useEffect(() => { if (!langs.includes(lang)) setLang(defLang); }, [langs.join(","), defLang]); // eslint-disable-line react-hooks/exhaustive-deps
  // Preview follows the editor, but any channel × language is one click away;
  // it snaps back to following whenever the editor moves.
  const [pvCh, setPvCh] = useState<string>(activeChannels[0] || "email");
  const [pvLang, setPvLang] = useState<string>(defLang);
  useEffect(() => { setPvCh(active); }, [active]);
  useEffect(() => { setPvLang(lang); }, [lang]);
  useEffect(() => { if (!activeChannels.includes(pvCh)) setPvCh(activeChannels[0] || "email"); }, [activeChannels.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!langs.includes(pvLang)) setPvLang(defLang); }, [langs.join(","), defLang]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch: Partial<MessageItem>) => setForm((f) => ({ ...f, ...patch }));
  const toggleChannel = (cid: string) => setForm((f) => {
    const on = f.channels.includes(cid);
    const channels = on ? f.channels.filter((x) => x !== cid) : [...f.channels, cid];
    return { ...f, channels: channels.length ? channels : f.channels };
  });
  const perCh = form.perChannel || {};
  const trOf = (l: string): MessageTranslation => (form.translations || {})[l] || {};
  const setTr = (l: string, patch: Partial<MessageTranslation>) => setForm((f) => ({ ...f, translations: { ...(f.translations || {}), [l]: { ...((f.translations || {})[l] || {}), ...patch } } }));
  const isDef = lang === defLang;
  /** Content accessors are (language, channel)-addressed. The default language
   *  reads the legacy root fields, so every pre-existing message is valid as-is. */
  const bodyIn = (l: string, cid: string) => l === defLang ? (perCh[cid] != null ? perCh[cid] : (form.body || "")) : (trOf(l).perChannel?.[cid] ?? "");
  const bodyFor = (cid: string) => bodyIn(lang, cid);
  const setPerCh = (cid: string, text: string) => isDef
    ? setForm((f) => ({ ...f, perChannel: { ...(f.perChannel || {}), [cid]: text } }))
    : setTr(lang, { perChannel: { ...(trOf(lang).perChannel || {}), [cid]: text } });
  const subjIn = (l: string) => l === defLang ? (form.subject || "") : (trOf(l).subject ?? "");
  const setSubject = (v: string) => isDef ? set({ subject: v }) : setTr(lang, { subject: v });
  /** This language's email design (root = default language) — step 2. */
  const designIn = (l: string): EmailDesign | undefined => l === defLang ? form.emailDesign : trOf(l).emailDesign;
  /** Step 3 — non-empty means this language's email is custom-HTML sourced. */
  const htmlIn = (l: string): string | undefined => l === defLang ? form.emailHtml : trOf(l).emailHtml;
  const openDesigner = async () => {
    try { const saved = await store.upsert({ ...form }); await fileIfNeeded(saved?.id ?? form.id); nav.go("messaging.compose.email", { id: saved.id, lang }); }
    catch (e) { console.warn("save before design failed:", e); toast({ tone: "danger", title: t("messaging.messageComposer.couldnTSaveTheMessage", "Couldn't save the message"), subtitle: t("messaging.messageComposer.retryInAMoment", "Retry in a moment.") }); }
  };

  // WhatsApp structured template state — PER LANGUAGE: Meta reviews each
  // language separately, so every language version carries its own template,
  // including its own approval status. Default language stays on the root field.
  const waIn = (l: string): WaTemplate => ({ ...WA_DEFAULT, ...(l === defLang ? (form.whatsapp || {}) : (trOf(l).whatsapp || {})) });
  const wa: WaTemplate = waIn(lang);
  const mutateWa = (fn: (cur: WaTemplate) => WaTemplate) => {
    const next = fn(waIn(lang));
    if (isDef) setForm((f) => ({ ...f, whatsapp: next }));
    else setTr(lang, { whatsapp: next });
  };
  const setWa = (patch: Partial<WaTemplate>) => mutateWa((cur) => ({ ...cur, ...patch }));
  const addWaBtn = () => mutateWa((cur) => cur.buttons.length >= 3 ? cur : { ...cur, buttons: [...cur.buttons, { id: waBtnId(), type: "quick_reply", label: "" }] });
  const setWaBtn = (id: string, patch: Partial<WaButton>) => mutateWa((cur) => ({ ...cur, buttons: cur.buttons.map((b) => b.id === id ? { ...b, ...patch } : b) }));
  const rmWaBtn = (id: string) => mutateWa((cur) => ({ ...cur, buttons: cur.buttons.filter((b) => b.id !== id) }));

  // Audience model depends on the channel mix. Broadcast channels (email/SMS)
  // target a ticket segment; consent channels (WhatsApp/IG/FB) only reach
  // opted-in contacts. When only consent channels are on, lock the selector.
  const hasBroadcast = activeChannels.some((c) => c === "email" || c === "sms");
  const hasConsentCh = activeChannels.some((c) => CONSENT_CHANNELS.includes(c));
  const audienceLocked = !hasBroadcast && hasConsentCh;
  useEffect(() => {
    if (audienceLocked && form.audience !== "Opted-in contacts") set({ audience: "Opted-in contacts" });
    else if (!audienceLocked && form.audience === "Opted-in contacts") set({ audience: "All" });
  }, [audienceLocked]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Calendar-invite template, per language like every other content field. */
  const icsIn = (l: string): IcsTemplate => ({ ...ICS_DEFAULT, ...(l === defLang ? (form.ics || {}) : (trOf(l).ics || {})) });
  const ics = icsIn(lang);
  /** Wallet-pass shape is fixed by the message (operator scope decision) — never
   *  user-chosen, so an appointment message can't accidentally ship a ticket. */
  const walletType: WalletPassType = walletTypeFor(form.id, form.wallet);
  const walletEligible = walletEligibleFor(form.id, form.wallet);
  const walletIn = (l: string): WalletTemplate => ({ ...WALLET_DEFAULTS[walletType], ...(l === defLang ? (form.wallet || {}) : (trOf(l).wallet || {})) });
  const wallet = walletIn(lang);
  /* Artefact modes (operator ruling 2026-09-01): calendar and wallet are
     SETTINGS on the email channel, not channels. One per message, never per
     language — the same .ics and the same pass serve every language, and a
     per-language copy would drift the first time someone edited one of them. */
  const artefacts = artefactsOf(form);
  const setArtefact = (id: ArtefactId, mode: ArtefactMode) =>
    setForm((f) => ({ ...f, artefacts: { ...artefactsOf(f), [id]: mode } }));
  const setWallet = (patch: Partial<WalletTemplate>) => {
    const next = { ...walletIn(lang), ...patch };
    if (isDef) setForm((f) => ({ ...f, wallet: next })); else setTr(lang, { wallet: next });
  };
  const branding = useBranding();
  const setIcs = (patch: Partial<IcsTemplate>) => {
    const next = { ...icsIn(lang), ...patch };
    if (isDef) setForm((f) => ({ ...f, ics: next })); else setTr(lang, { ics: next });
  };

  /* Completeness: a channel counts as done in a language when its body (and for
     email, its subject) is non-empty. Feeds the language dots + the live gate. */
  const langContentOk = (l: string, cid: string) => cid === "email" ? !!(subjIn(l).trim() && (bodyIn(l, cid).trim() || emailHasContent(designIn(l)) || !!htmlIn(l)))
    : cid === "calendar" ? !!icsIn(l).summary.trim()
    : cid === "wallet" ? !!walletIn(l).primaryValue.trim()
    : !!bodyIn(l, cid).trim();
  const missingContent = langs.flatMap((l) => activeChannels.filter((cid) => STUB_BUILDER_CHANNELS.indexOf(cid) < 0 && !langContentOk(l, cid)).map((cid) => `${localeDisplayName(l)} · ${MSG_CHANNEL(cid).name}`));
  /* Artefacts are no longer channels, so they are no longer covered by the loop
     above — but a carried artefact with no content is exactly the gap the old
     per-channel gate caught. Checked per language because the SUMMARY and the
     pass's primary value are customer-facing copy that translates; the MODE
     itself is message-wide. An artefact set to `off` is not incomplete. */
  const missingArtefacts = langs.flatMap((l) => {
    const out: string[] = [];
    if (artefacts.calendar !== "off" && !icsIn(l).summary.trim()) out.push(`${localeDisplayName(l)} · ${MSG_CHANNEL("calendar").name}`);
    if (artefacts.wallet !== "off" && walletEligible && !walletIn(l).primaryValue.trim()) out.push(`${localeDisplayName(l)} · ${MSG_CHANNEL("wallet").name}`);
    return out;
  });
  const addLanguage = (tag: string) => { if (!tag || langs.includes(tag)) return; set({ languages: [...langs, tag], defaultLanguage: defLang }); setLang(tag); };
  const removeLanguage = (tag: string) => {
    if (tag === defLang) return;
    const next = { ...(form.translations || {}) }; delete next[tag];
    set({ languages: langs.filter((l) => l !== tag), translations: next, defaultLanguage: defLang });
    if (lang === tag) setLang(defLang);
  };

  const saveDisabled = !form.name.trim() || activeChannels.length === 0;
  // Created from inside a folder ⇒ the new message lands in that folder (operator,
  // 2026-08-05). Filed once, on the FIRST successful save — the composer autosaves,
  // so a message abandoned before it has a name is never filed.
  const bornInFolder = (nav.params && (nav.params.folder as string | undefined)) || null;
  const col = useCollections("messages");
  const filedRef = useRef(false);
  const fileIfNeeded = async (savedId: string) => {
    if (!bornInFolder || !isNew || filedRef.current || !savedId) return;
    filedRef.current = true;
    try { await col.setItems(bornInFolder, { add: [savedId] }); }
    catch (e) { filedRef.current = false; console.warn("filing new message failed:", e); }
  };
  const saveAndFile = async () => { const saved = await store.upsert({ ...form }); await fileIfNeeded(saved?.id ?? form.id); return saved; };
  // Two-status lifecycle (operator, 2026-08-04): draft → live. A live message is
  // locked — all fields visible but disabled — until sent back to draft.
  const isLive = form.status === "live";
  const locked = viewOnly || isLive;
  const autoStatus = useAutosave({ enabled: !locked && !saveDisabled, data: form, save: saveAndFile });
  // "Make Live" makes the message an available library asset that Events and
  // message flows can use. It then locks for editing (Back to Draft to edit).
  const makeLive = async () => {
    // Live is a unit (operator, 2026-07-30): every language × channel must have
    // content before the message becomes a usable library asset. Carried
    // artefacts count — same gate, they simply stopped being channels.
    const gaps = [...missingContent, ...missingArtefacts];
    if (gaps.length) {
      toast({ tone: "danger", title: t("messaging.messageComposer.notReadyToGoLive", "Not ready to go live"), subtitle: `${t("messaging.messageComposer.missingContentFor", "Missing content for")} ${gaps.slice(0, 3).join(" · ")}${gaps.length > 3 ? ` · +${gaps.length - 3}` : ""}` });
      return;
    }
    const next = { ...form, status: "live" as const };
    try {
      const saved = await store.upsert(next);
      await fileIfNeeded(saved?.id ?? next.id);
      setForm(next);
      toast({ tone: "success", title: t("messaging.messageComposer.messageIsLive", "Message is live"), subtitle: t("messaging.messageComposer.availableToUseInEventsAndMessageFlowsSendItBackTo", "Available to use in Events and message flows. Send it back to draft to edit.") });
    } catch (e) {
      console.warn("make-live failed:", e);
      toast({ tone: "danger", title: t("messaging.messageComposer.couldnTMakeTheMessageLive", "Couldn't make the message live"), subtitle: t("messaging.messageComposer.itSStillADraftRetryInAMoment", "It's still a draft. Retry in a moment.") });
    }
  };
  // "Back to Draft" unlocks editing and removes the message from flow/event use
  // until it is made live again.
  const backToDraft = async () => {
    const next = { ...form, status: "draft" as const };
    try {
      await store.upsert(next);
      setForm(next);
      toast({ tone: "info", title: t("messaging.messageComposer.backToDrafta16683e7", "Back to draft"), subtitle: t("messaging.messageComposer.thisMessageIsEditableAgainAndWonTBeUsableInFlows", "This message is editable again and won't be usable in flows until you make it live.") });
    } catch (e) {
      console.warn("back-to-draft failed:", e);
      toast({ tone: "danger", title: t("messaging.messageComposer.couldnTUnlockTheMessage", "Couldn't unlock the message"), subtitle: t("messaging.messageComposer.itSStillLiveRetryInAMoment", "It's still live. Retry in a moment.") });
    }
  };
  const [flowOpen, setFlowOpen] = useState(false);
  const openAddToFlow = async () => { try { await store.upsert({ ...form }); setFlowOpen(true); } catch (e) { console.warn("save before add-to-flow failed:", e); toast({ tone: "danger", title: t("messaging.messageComposer.couldnTSaveTheMessage", "Couldn't save the message"), subtitle: t("messaging.messageComposer.retryInAMoment", "Retry in a moment.") }); } };

  const ch = MSG_CHANNEL(active);
  const isShort = active === "sms" || active === "whatsapp";
  // Preview resolution — a missing language falls back to the default language,
  // exactly the send-time rule (operator decision 2026-07-30), and says so.
  const pvC = MSG_CHANNEL(pvCh);
  /** Message context decides which tokens can resolve (person + org always). */
  const tokCtx = contextOf(form.id + " " + form.name);
  const quickFor = (cid: string) => tokCtx === "appointment" ? ["first_name", "service", "time", "location", "manage_url"]
    : tokCtx === "queue" ? ["first_name", "queue_name", "position", "est_wait"]
    : tokCtx === "event" ? ["first_name", "event_name", "event_date", "venue", cid === "email" ? "ticket_url" : "register_url"]
    : ["first_name", "org_name", "book_url"];
  /** Preview shows sample values by default; Show tokens reveals the raw text. */
  const [pvRawTokens, setPvRawTokens] = useState(false);
  const shown = (s: string) => (pvRawTokens ? s : resolveSamples(s));
  const pvRaw = bodyIn(pvLang, pvCh);
  const pvFellBack = pvLang !== defLang && !pvRaw.trim();
  const pvBody = pvFellBack ? bodyIn(defLang, pvCh) : pvRaw;
  const pvSubj = pvLang !== defLang && !subjIn(pvLang).trim() ? subjIn(defLang) : subjIn(pvLang);
  const pvWa = pvLang === defLang ? waIn(defLang) : ((form.translations || {})[pvLang]?.whatsapp ? waIn(pvLang) : waIn(defLang));
  const pvDesign = pvLang === defLang ? form.emailDesign : ((form.translations || {})[pvLang]?.emailDesign ?? form.emailDesign);
  const pvCustomHtml = pvLang === defLang ? form.emailHtml : ((form.translations || {})[pvLang]?.emailHtml ?? form.emailHtml);

  /* A platform-owned appointment message (CC 2026-09-03 §3). The trigger is
     fixed — it is what the platform fires on, not a field — so it is stated,
     never edited, and the save's consequence is stated with it. */
  const isAppt = form.lineage === "appointment_comms";

  /* A design that is NOT the legacy client schema must never reach
     `renderEmailHtml` (CC 2026-09-03 addendum 3): `emailDesign` on the wire is
     now the platform block document — the ten `email_*` blocks, no `theme` — and
     the legacy renderer reads `theme.bg`, which is the crash the deployed
     composer shows on the live confirmation. The server render is the path for
     those documents; when it is unavailable we say so rather than guess. */
  const pvLegacyOk = !!pvDesign && !!(pvDesign as unknown as { theme?: unknown }).theme;

  /* The email preview is the SERVER's render (POST …/render), so preview and
     send come out of one renderer instead of two that agree until they don't.
     The client renderer stays for the case the op cannot serve: a message that
     has never been saved has no messageId to render. Client-side `{token}`
     samples are still ours — the platform does not resolve those. */
  const savedId = !isNew && form.id && !form.id.startsWith("m_new_") ? form.id : null;
  const [pvServer, setPvServer] = useState<MsgRender | null>(null);
  const [pvServerFailed, setPvServerFailed] = useState(false);
  useEffect(() => {
    if (!savedId || pvCh !== "email") { setPvServer(null); setPvServerFailed(false); return; }
    let live = true;
    /* Debounced 500 ms at their ask: the effect re-keys on every autosave tick,
       and a render is an asset read plus a block render on their side. */
    const timer = setTimeout(() => {
      renderMessage(savedId, { locale: pvLang })
        .then((r) => { if (live) { setPvServer(r.source === "none" ? null : r); setPvServerFailed(false); } })
        .catch((e) => { if (live) { setPvServer(null); setPvServerFailed(true); console.warn("server render unavailable:", e); } });
    }, 500);
    return () => { live = false; clearTimeout(timer); };
  }, [savedId, pvCh, pvLang, autoStatus]);
  /** Fields the platform dropped on the last write, named back to the author. */
  const writeWarnings = store.warnings();

  return (
    <Screen rail="messaging">
      <PageHeader
        title={isNew ? t("messaging.library.newMessage", "New message") : ((viewOnly || isLive) ? form.name || t("builder.eventFlowBuilder.message", "Message") : t("messaging.messageComposer.editFormNameMessage", { value0: form.name || t("builder.eventFlowBuilder.message", "Message") }))}
        subtitle={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>{isAppt && form.trigger ? <><span style={{ fontSize: 12, color: T.n2 }}>{triggerReading(form.trigger, form.offsetMinutes)}</span><span style={{ fontFamily: T.fontMono, fontSize: 12, color: T.soft }}>{form.trigger}</span> · </> : null}{activeChannels.length} {t("messaging.messageComposer.channel", "channel")}{activeChannels.length === 1 ? "" : t('messaging.messageComposer.text4')} · {langs.length} {langs.length === 1 ? t("messaging.messageComposer.language", "language") : t("messaging.messageComposer.languages", "languages")} {msgStatusPill(form.status)}</span>}
        breadcrumb={<span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => nav.go("messaging")}><Icon.arrow dir="left" size={11} /> {t("messaging.messageComposer.messaging", "Messaging")}</span>}
        actions={viewOnly ? (canWrite ? <Btn icon={<Icon.edit size={14} />} onClick={() => nav.go("messaging.compose", { id: form.id })}>{t("common.actions.edit", "Edit")}</Btn> : null)
          : isLive ? <>
            <Btn variant="secondary" onClick={() => nav.go("messaging")}>{t("common.actions.close", "Close")}</Btn>
            <Btn variant="secondary" icon={<Icon.list size={14} />} onClick={openAddToFlow}>{t("messaging.messageComposer.addToFlow", "Add to Flow")}</Btn>
            <Btn icon={<Icon.edit size={14} />} onClick={backToDraft}>{t("messaging.messageComposer.backToDraft", "Back to Draft")}</Btn>
          </> : <>
            <AutosaveBadge status={autoStatus} />
            <Btn variant="secondary" onClick={() => nav.go("messaging")}>{t("common.actions.cancel", "Cancel")}</Btn>
            <Btn variant="secondary" icon={<Icon.list size={14} />} disabled={saveDisabled} onClick={openAddToFlow}>{t("messaging.messageComposer.addToFlow", "Add to Flow")}</Btn>
            <Btn icon={<Icon.check size={14} />} disabled={saveDisabled} onClick={makeLive}>{t("messaging.messageComposer.makeLive", "Make Live")}</Btn>
          </>}
      />
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "minmax(440px,1fr) minmax(320px,400px)", minHeight: 0, borderTop: `1px solid ${T.border}` }}>
        {/* editor */}
        <div style={{ borderRight: `1px solid ${T.border}`, overflow: "auto", padding: "20px 24px 40px", background: T.canvas }}>
          {isAppt ? (
            <div style={{ display: "flex", gap: 10, padding: "10px 14px", marginBottom: 16, background: T.p4, border: `1px solid ${T.p3}`, borderRadius: 10, fontSize: 12.5, color: T.n1, lineHeight: 1.5 }}>
              <span style={{ color: T.p1, flexShrink: 0, display: "inline-flex", marginTop: 1 }}><Icon.info size={15} /></span>
              <span><b>{t("messaging.messageComposer.apptOwnedTitle", "Sent by the platform.")}</b>{" "}
                {t("messaging.messageComposer.apptOwnedBody", "This message goes out when its trigger fires; the trigger itself is not editable here. Saving publishes a new version and the next send uses it.")}
                {form.trigger ? <> <span style={{ fontFamily: T.fontMono, fontSize: 11.5 }}>{form.trigger}</span></> : null}</span>
            </div>
          ) : null}
          {writeWarnings.length ? (
            /* A field the platform dropped has to be visible where it was typed.
               `warnings[]` names each one; silence here is how "it saved" and
               "it stored what you wrote" came apart. */
            <div style={{ display: "flex", gap: 10, padding: "10px 14px", marginBottom: 16, background: T.orange2, border: `1px solid ${alpha(T.orange1, "40")}`, borderRadius: 10, fontSize: 12.5, color: T.n1, lineHeight: 1.5 }}>
              <span style={{ color: T.orange1, flexShrink: 0, display: "inline-flex", marginTop: 1 }}><Icon.warn size={15} /></span>
              <span><b>{t("messaging.messageComposer.warningsTitle", "Not everything was stored.")}</b>{" "}
                {writeWarnings.map((w) => w.field || w.code).join(", ")} — {t("messaging.messageComposer.warningsBody", "the platform does not model these fields yet, so they were dropped on save.")}</span>
            </div>
          ) : null}
          {isLive ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", marginBottom: 16, background: "#E9F7EF", border: "1px solid #B7E4C9", borderRadius: 10, fontSize: 12.5, color: "#0F6B3C" }}>
              <Icon.check size={15} />
              <span><b>{t("messaging.messageComposer.thisMessageIsLive", "This message is live.")}</b> {t("messaging.messageComposer.fieldsAreLockedWhileItSAvailableToEventsAndFlowsClick", "Fields are locked while it’s available to Events and flows — click")}{" "}<b>{t("messaging.messageComposer.backToDraft", "Back to Draft")}</b> {t("messaging.messageComposer.toEdit", "to edit.")}</span>
            </div>
          ) : null}
          <fieldset disabled={locked} style={{ border: "none", margin: 0, padding: 0, minInlineSize: "auto", opacity: locked ? 0.65 : 1 }}>
          <Card style={{ padding: 18, display: "flex", flexDirection: "column", gap: 14, marginBottom: 16 }}>
            <div><div style={msgLabel}>{t("messaging.messageComposer.messageName", "Message name")}<Req /></div><input autoFocus={isNew} value={form.name} onChange={(e) => set({ name: e.target.value })} aria-required="true" aria-invalid={!form.name.trim()} placeholder={t("messaging.messageComposer.internalNameEGDoorsOpenReminder", "Internal name, e.g. Doors open reminder")} style={msgInput(!form.name.trim() ? { borderColor: T.red1 } : {})} />
              <FieldError show={!form.name.trim()}>{t("messaging.messageComposer.nameRequired", "Give the message a name so you can find it later.")}</FieldError>
            </div>
            <div>
              <div style={msgLabel}>{t("messaging.messageComposer.audience", "Audience")}</div>
              {audienceLocked ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, height: 40, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8, background: T.n6, color: T.n1, fontSize: 14 }}>
                    <Icon.check size={14} style={{ color: "#128C3E" }} /> {t("messaging.messageComposer.optedInContacts", "Opted-in contacts")}</div>
                  <div style={{ fontSize: 11.5, color: T.soft, marginTop: 6 }}>{t("messaging.messageComposer.whatsappInstagramAndFacebookCanOnlyReachPeopleWhoOptedInOr", "WhatsApp, Instagram and Facebook can only reach people who opted in or messaged you in the last 24 hours — not a ticket segment.")}</div>
                </>
              ) : (
                <>
                  <div style={{ position: "relative" }}>
                    <select value={form.audience || "All"} onChange={(e) => set({ audience: e.target.value })} style={msgInput({ appearance: "none", WebkitAppearance: "none", paddingRight: 34, cursor: "pointer" })}>
                      <option value="All">{t("messaging.messageComposer.allEveryoneRegistered", "All — everyone registered")}</option>
                      {kindsPresent.length ? (
                        <optgroup label={t("messaging.messageComposer.ticketCategories", "Ticket categories")}>
                          {kindsPresent.map((k) => <option key={k.id} value={`${k.label} tickets`}>{k.label} {t("messaging.messageComposer.tickets", "tickets")}</option>)}
                        </optgroup>
                      ) : null}
                      {ticketTypes.length ? (
                        <optgroup label={t("messaging.messageComposer.ticketTypes", "Ticket types")}>
                          {ticketTypes.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
                        </optgroup>
                      ) : null}
                    </select>
                    <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: T.n2 }}>{CARET}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: T.soft, marginTop: 6 }}>
                    {hasConsentCh
                      ? t('messaging.messageComposer.text5')
                      : t('messaging.messageComposer.text6')}
                  </div>
                </>
              )}
            </div>
          </Card>

          <div style={{ fontSize: 13, fontWeight: 700, color: T.n1, margin: "4px 2px 10px" }}>{t("messaging.messageComposer.channels", "Channels")}<Req /><span style={{ fontWeight: 400, color: T.soft }}> · {t("messaging.messageComposer.atLeastOne", "at least one")}</span></div>
          {/* One row, five channels — compact by operator request (2026-07-31). */}
          <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
            {BUILDER_CHANNELS.map((c) => {
              const on = form.channels.includes(c.id);
              // Wallet is gated PER MESSAGE, not globally (2026-07-31): only the
              // three "you have a thing" confirmations get a pass — a pass tied
              // to a reminder or cancellation would have nothing to attach to.
              const stub = c.id === "wallet" && !walletEligible;
              return (
                /* ui-raw: pill toggle in a chip row — ChipToggleGroup's pattern (group 6) */
                <button key={c.id} type="button" disabled={stub} title={stub ? t("messaging.messageComposer.walletNotEligible", "Wallet passes are for booking confirmations, event registrations, and queue-join messages") : undefined}
                  onClick={() => !stub && toggleChannel(c.id)} data-testid={`mcomposer-ch-${c.id}`}
                  style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "7px 6px", borderRadius: 8, border: `1.5px solid ${on ? c.hue : T.border}`, background: stub ? T.n6 : on ? c.hue + "12" : T.white, cursor: stub ? "not-allowed" : "pointer", fontFamily: T.font, opacity: stub ? 0.55 : 1 }}>
                  <ChGlyph id={c.id} size={14} color={on ? c.hue : T.n2} />
                  <span style={{ fontSize: 12.5, fontWeight: on ? 700 : 500, color: on ? c.hue : T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</span>
                  {on ? <Icon.check size={12} style={{ color: c.hue, flexShrink: 0 }} /> : null}
                </button>
              );
            })}
          </div>

          {/* Languages — chips with completeness dots; content below is edited
              per (language × channel). Offer list is the shipped catalog until
              the tenant-configured list exists (REQ-112b). */}
          <div style={{ fontSize: 13, fontWeight: 700, color: T.n1, margin: "4px 2px 10px" }}>{t("messaging.messageComposer.languagesTitle", "Languages")}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
            {langs.map((l) => {
              const on = lang === l;
              const done = activeChannels.every((cid) => langContentOk(l, cid));
              return (
                /* ui-raw: pill toggle in a chip row — ChipToggleGroup's pattern (group 6) */
                <button key={l} type="button" onClick={() => setLang(l)} data-testid={`mcomposer-lang-${l}`} aria-pressed={on}
                  style={{ display: "inline-flex", alignItems: "center", gap: 7, minHeight: 36, padding: "0 12px", borderRadius: 999, border: `1.5px solid ${on ? T.p1 : T.border}`, background: on ? T.p4 : T.white, color: on ? T.p1 : T.n1, fontFamily: T.font, fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
                  <span aria-hidden title={done ? t("messaging.messageComposer.complete", "Complete") : t("messaging.messageComposer.missingContent", "Missing content")} style={{ width: 7, height: 7, borderRadius: 999, background: done ? T.green1 : T.n4, flexShrink: 0 }} />
                  {localeDisplayName(l)}
                  {l === defLang ? <span style={{ fontSize: 10.5, fontWeight: 700, color: on ? T.p1 : T.soft }}>· {t("messaging.messageComposer.default", "Default")}</span> : null}
                  {l !== defLang ? <span role="button" aria-label={t("messaging.messageComposer.removeLanguage", "Remove language")} onClick={(e) => { e.stopPropagation(); removeLanguage(l); }} style={{ display: "inline-flex", alignItems: "center", marginLeft: 2, color: T.n3 }}><Icon.x size={11} /></span> : null}
                </button>
              );
            })}
            <div style={{ position: "relative" }}>
              <select value="" onChange={(e) => addLanguage(e.target.value)} aria-label={t("messaging.messageComposer.addLanguage", "Add language")} data-testid="mcomposer-add-lang"
                style={msgInput({ width: "auto", height: 36, fontSize: 12.5, fontWeight: 600, appearance: "none", WebkitAppearance: "none", paddingRight: 32, cursor: "pointer" })}>
                <option value="">{t("messaging.messageComposer.addLanguagePlus", "+ Add language")}</option>
                {availableLocales().filter((l) => !langs.includes(l)).map((l) => <option key={l} value={l}>{localeDisplayName(l)}</option>)}
              </select>
              <span style={{ position: "absolute", right: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: T.n2 }}>{CARET}</span>
            </div>
          </div>

          {/* per-channel content tabs */}
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ display: "flex", gap: 2, padding: "8px 8px 0", borderBottom: `1px solid ${T.border}`, flexWrap: "wrap" }}>
              {activeChannels.map((cid) => {
                const c = MSG_CHANNEL(cid); const on = active === cid;
                return (
                  /* ui-raw: channel tab strip (2px underline on active, per-channel hue) — Tabs' pattern (group 8) */
                  <button key={cid} onClick={() => setActive(cid)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 12px", border: "none", borderBottom: `2px solid ${on ? c.hue : "transparent"}`, background: "transparent", color: on ? T.n1 : T.soft, fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: T.font }}>
                    <ChGlyph id={cid} size={13} color={on ? c.hue : T.soft} />{c.name}
                  </button>
                );
              })}
            </div>
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
              {!isDef ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", background: T.p4, border: `1px solid ${T.p3}`, borderRadius: 8, fontSize: 12.5, color: T.n1 }}>
                  <Icon.info size={14} style={{ color: T.p1, flexShrink: 0 }} />
                  <span style={{ flex: 1, lineHeight: 1.45 }}>{t("messaging.messageComposer.editingLangVersion", "Editing the {lang} version. Anything left empty falls back to {def} at send time.").replace("{lang}", localeDisplayName(lang)).replace("{def}", localeDisplayName(defLang))}</span>
                  <Btn size="sm" variant="secondary" disabled title={t("messaging.messageComposer.mtHeld", "Machine translation needs the translate op — REQ-112c")}>{t("messaging.messageComposer.autoTranslate", "Auto-translate")}</Btn>
                </div>
              ) : null}
              {active === "whatsapp" ? (
                <WaEditor wa={wa} body={bodyFor("whatsapp")} onBody={(t) => setPerCh("whatsapp", t)} setWa={setWa} addBtn={addWaBtn} setBtn={setWaBtn} rmBtn={rmWaBtn} />
              ) : (
                <>
                  {active === "email" ? <><TokenField value={subjIn(lang)} onChange={setSubject} ctx={tokCtx} ariaRequired invalid={!subjIn(lang).trim()} placeholder={t("messaging.messageComposer.subjectLine", "Subject line") + " *"} quick={["first_name", tokCtx === "appointment" ? "service" : "event_name"]} testid="mcomposer-subject" />
                    <FieldError show={!subjIn(lang).trim()}>{t("messaging.messageComposer.subjectRequired", "A subject line is required for every language.")}</FieldError></> : null}
                  {active === "email" && isDef ? (
                    /* Preheader is modelled on the message asset (PR #1652) and
                       writable on appointment items. It is the second line an
                       inbox shows, so it belongs next to the subject, not in a
                       settings panel. Default language only for now — locale
                       content on appointment items is one row per locale
                       upstream, so a per-locale preheader has nowhere to go. */
                    <div>
                      <div style={msgLabel}>{t("messaging.messageComposer.preheader", "Preheader")}</div>
                      <TokenField value={form.preheader ?? ""} onChange={(v) => set({ preheader: v })} ctx={tokCtx} quick={quickFor("email")} placeholder={t("messaging.messageComposer.preheaderPlaceholder", "The preview line after the subject in an inbox")} testid="mcomposer-preheader" />
                    </div>
                  ) : null}
                  {active === "email" && isAppt ? (
                    /* A platform-owned item's design is the platform BLOCK
                       DOCUMENT (the ten `email_*` blocks, per locale, no
                       `theme`), which the legacy designer cannot read — opening
                       it is the same crash the deployed composer showed. The
                       control is REMOVED, and the reason is stated where it
                       stood: a disabled button with no explanation is a worse
                       answer than a sentence (CC 2026-09-03 §5). */
                    <div style={{ border: `1px solid ${T.border}`, background: T.n6, borderRadius: 10, padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
                      <span style={{ width: 34, height: 34, borderRadius: 9, background: T.n5, color: T.n2, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.mail size={15} /></span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{t("messaging.messageComposer.platformDesign", "Platform design")}</div>
                        <div style={{ fontSize: 11.5, color: T.soft, marginTop: 2, lineHeight: 1.45 }}>{t("messaging.messageComposer.platformDesignHint", "Design edits arrive with the block editor. Subject and preheader are editable here now, and the preview is the platform's own render.")}</div>
                      </div>
                    </div>
                  ) : null}
                  {active === "email" && !isAppt && (designIn(lang) || htmlIn(lang)) ? (
                    <div style={{ border: `1.5px solid ${T.p3}`, background: T.p4, borderRadius: 10, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ width: 34, height: 34, borderRadius: 9, background: T.p1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.edit size={15} /></span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{htmlIn(lang) ? t("messaging.messageComposer.customHtmlEmail", "Custom HTML email") : <>{t("messaging.messageComposer.designedEmail", "Designed email")} · {(designIn(lang) as EmailDesign).blocks.length} {t("messaging.messageComposer.blocks", "blocks")}</>}</div>
                        <div style={{ fontSize: 11.5, color: T.soft, marginTop: 2, lineHeight: 1.45 }}>{htmlIn(lang) ? t("messaging.messageComposer.customHtmlEmailHint", "Code-sourced — sent exactly as written, with an extracted plain-text alternative.") : t("messaging.messageComposer.designedEmailHint", "Outlook-safe HTML with an auto-generated plain-text alternative — shown in the preview panel.")}</div>
                      </div>
                      <Btn onClick={() => void openDesigner()} testid="mcomposer-edit-design">{htmlIn(lang) ? t("messaging.messageComposer.editCode", "Edit code") : t("messaging.messageComposer.editDesign", "Edit design")}</Btn>
                    </div>
                  ) : null}
                  {active === "email" && !isAppt && !designIn(lang) && !htmlIn(lang) ? (
                    /* ui-raw: dashed launcher tile for the email designer — not Button's solid/ghost treatment; nearest is ChoiceTile */
                    <button type="button" onClick={() => void openDesigner()} disabled={saveDisabled} data-testid="mcomposer-design-email" style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", borderRadius: 10, border: `1.5px dashed ${T.p2}`, background: T.white, cursor: saveDisabled ? "not-allowed" : "pointer", fontFamily: T.font, textAlign: "left", opacity: saveDisabled ? 0.6 : 1 }}>
                      <span style={{ color: T.p1, display: "inline-flex", flexShrink: 0 }}><Icon.plus size={15} /></span>
                      <span style={{ flex: 1 }}>
                        <span style={{ display: "block", fontSize: 13, fontWeight: 700, color: T.p1 }}>{t("messaging.messageComposer.designThisEmail", "Design this email (WYSIWYG)")}</span>
                        <span style={{ display: "block", fontSize: 11.5, color: T.soft, marginTop: 2 }}>{saveDisabled ? t("messaging.messageComposer.nameFirst", "Name the message first.") : t("messaging.messageComposer.designThisEmailHint", "Blocks, brand styling, Outlook-safe output — or keep it plain text below.")}</span>
                      </span>
                    </button>
                  ) : null}
                  {/* Artefacts — INSIDE the Email tab, beside "Designed email · N
                      blocks", not beside the channel list (their open question,
                      §7). Beside the channels it would read as a fourth and fifth
                      channel, which is exactly the reading the ruling removes;
                      here it reads as what it is — something this email carries.
                      One control each, three states, message-wide. */}
                  {active === "email" ? (
                    <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: "13px 15px", display: "flex", flexDirection: "column", gap: 14 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: T.n1 }}>{t("messaging.messageComposer.artefacts", "What this email carries")}
                        <span style={{ fontWeight: 400, color: T.soft, marginLeft: 6 }}>{t("messaging.messageComposer.artefactsHint", "· same for every language")}</span></div>
                      {(["calendar", "wallet"] as ArtefactId[]).map((aid) => {
                        const mode = artefacts[aid];
                        const opts = ARTEFACT_MODES[aid];
                        const title = aid === "calendar"
                          ? t("messaging.messageComposer.artefactCalendar", "Calendar entry (.ics)")
                          : t("messaging.messageComposer.artefactWallet", "Apple Wallet pass");
                        /* Wallet is still gated PER MESSAGE: a pass tied to a
                           reminder or a cancellation has nothing to attach to. */
                        if (aid === "wallet" && !walletEligible) return null;
                        return (
                          <div key={aid} style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                              <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n1, flex: 1, minWidth: 120 }}>{title}</span>
                              {/* ui-raw: pill toggle in a chip row — ChipToggleGroup's pattern (group 6) */}
                              <span style={{ display: "inline-flex", gap: 4, flexShrink: 0 }}>
                                {opts.map((o) => (
                                  <button key={o} type="button" disabled={locked} onClick={() => setArtefact(aid, o)}
                                    data-testid={`mcomposer-artefact-${aid}-${o}`}
                                    style={{ minHeight: 26, padding: "0 10px", borderRadius: 999, border: `1px solid ${mode === o ? T.p1 : T.border}`, background: mode === o ? T.p4 : T.white, color: mode === o ? T.p1 : T.n2, fontFamily: T.font, fontSize: 11.5, fontWeight: 600, cursor: locked ? "default" : "pointer" }}>
                                    {ARTEFACT_MODE_LABEL[o]}
                                  </button>
                                ))}
                              </span>
                            </div>
                            <div style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.5 }}>{ARTEFACT_MODE_HINT[aid][mode]}</div>
                            {/* The artefact's own content editor, only while it
                                is actually carried — the old channel tab, now
                                where the setting that turns it on lives. */}
                            {aid === "calendar" && mode !== "off" ? <IcsEditor ics={ics} onIcs={setIcs} ctx={tokCtx} /> : null}
                            {aid === "wallet" && mode !== "off" ? <WalletEditor tpl={wallet} onChange={setWallet} type={walletType} ctx={tokCtx} /> : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  {active === "email" && (designIn(lang) || htmlIn(lang)) ? null : (<>
                  <TokenField value={bodyFor(active)} onChange={(v) => setPerCh(active, v)} ctx={tokCtx} multiline
                    placeholder={isShort ? t("messaging.messageComposer.shortChNameMessage", { value0: ch.name }) : t("messaging.messageComposer.writeYourChNameContent", { value0: ch.name })}
                    /* 1600, the contract's own SMS ceiling (`MESSAGE_BODY_MAX_CHARS.sms`),
                       not the historical 320 this field used to cap at (operator,
                       2026-09-16): a shorter editor limit silently truncates
                       content the platform already stores and accepts. */
                    rows={isShort ? 4 : 8} maxLength={active === "sms" ? SMS_MAX : undefined}
                    ariaRequired invalid={!bodyFor(active).trim()} quick={quickFor(active)} testid="mcomposer-body" />
                  {active === "sms" ? <SmsBudget body={bodyFor("sms")} onFix={locked ? undefined : (v) => setPerCh("sms", v)} /> : (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: T.soft }}>
                    <span>{t('messaging.messageComposer.text8')}</span>
                  </div>
                  )}
                  <FieldError show={!bodyFor(active).trim()}>{t("messaging.messageComposer.bodyRequired", "This channel needs content in every language before the message can go live.")}</FieldError>
                  </>)}
                </>
              )}
            </div>
          </Card>
          </fieldset>
        </div>

        {/* preview — any channel × any language, one click (operator, 2026-07-30).
            Follows the editor; explicit picks override until the editor moves. */}
        <div style={{ overflow: "auto", padding: 24, background: T.canvas, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ flex: 1 }}>{t("messaging.messageComposer.preview2", "Preview")}</span>
            {/* ui-raw: pill toggle in a chip row — ChipToggleGroup's pattern (group 6) */}
            <button type="button" onClick={() => setPvRawTokens(!pvRawTokens)} data-testid="token-preview-toggle"
              style={{ minHeight: 24, padding: "0 9px", borderRadius: 999, border: `1px solid ${pvRawTokens ? T.n1 : T.border}`, background: pvRawTokens ? T.n1 : T.white, color: pvRawTokens ? T.white : T.n2, fontFamily: T.font, fontSize: 11, fontWeight: 600, cursor: "pointer", textTransform: "none", letterSpacing: 0 }}>
              {pvRawTokens ? t("messaging.tokens.showValues", "Show values") : t("messaging.tokens.showTokens", "Show tokens")}
            </button>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {activeChannels.map((cid) => { const c = MSG_CHANNEL(cid); const on = pvCh === cid; return (
              /* ui-raw: pill toggle in a chip row — ChipToggleGroup's pattern (group 6) */
              <button key={cid} onClick={() => setPvCh(cid)} data-testid={`mcomposer-pv-ch-${cid}`} aria-pressed={on} style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 30, padding: "0 11px", borderRadius: 999, border: `1.5px solid ${on ? c.hue : T.border}`, background: on ? c.hue + "12" : T.white, color: on ? c.hue : T.n1, fontFamily: T.font, fontSize: 12, fontWeight: 600, cursor: "pointer" }}><ChGlyph id={cid} size={12} color={on ? c.hue : T.n2} />{c.name}</button>
            ); })}
          </div>
          {langs.length > 1 ? (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {langs.map((l) => { const on = pvLang === l; return (
                /* ui-raw: pill toggle in a chip row — ChipToggleGroup's pattern (group 6) */
                <button key={l} onClick={() => setPvLang(l)} data-testid={`mcomposer-pv-lang-${l}`} aria-pressed={on} style={{ minHeight: 28, padding: "0 10px", borderRadius: 999, border: `1px solid ${on ? T.n1 : T.border}`, background: on ? T.n1 : T.white, color: on ? T.white : T.n2, fontFamily: T.font, fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>{localeDisplayName(l)}</button>
              ); })}
            </div>
          ) : null}
          {pvFellBack ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: T.orange2, border: `1px solid ${alpha(T.orange1, "40")}`, borderRadius: 8, fontSize: 12, color: T.n1 }}>
              <Icon.info size={13} style={{ color: T.orange1, flexShrink: 0 }} />
              <span>{t("messaging.messageComposer.noVersionFallsBack", "No {lang} version yet — recipients get the {def} content, shown here.").replace("{lang}", localeDisplayName(pvLang)).replace("{def}", localeDisplayName(defLang))}</span>
            </div>
          ) : null}
          <Card style={{ padding: 0, overflow: "hidden", flexShrink: 0 }}>
            <div style={{ height: 4, background: pvC.hue }} />
            <div style={{ padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <span style={{ width: 30, height: 30, borderRadius: 999, background: pvC.hue + "18", color: pvC.hue, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><ChGlyph id={pvC.id} size={15} color={pvC.hue} /></span>
                <div><div style={{ fontSize: 13, fontWeight: 700, color: T.n1 }}>{t("messaging.messageComposer.acmeLive", "Acme Live")}</div><div style={{ fontSize: 11, color: T.soft }}>{t("messaging.messageComposer.via", "via")}{" "}{pvC.name} · {localeDisplayName(pvLang)}</div></div>
              </div>
              {pvCh === "whatsapp" ? (
                <WaPreview wa={pvWa} body={shown(pvBody)} />
              ) : pvCh === "calendar" ? (
                <IcsPreview ics={icsIn(pvLang)} raw={pvRawTokens} />
              ) : pvCh === "wallet" ? (
                <WalletPreview tpl={walletIn(pvLang)} type={walletType} raw={pvRawTokens} branding={branding} />
              ) : pvCh === "email" && (pvServer || pvCustomHtml || pvLegacyOk) ? (
                <>
                  {(pvServer?.subject || pvSubj) ? <div style={{ fontSize: 14, fontWeight: 700, color: T.n1, marginBottom: 8 }}>{pvServer?.subject || pvSubj}</div> : null}
                  <iframe title={t("messaging.messageComposer.emailPreview", "Email preview")} srcDoc={pvServer ? pvServer.html : (pvCustomHtml ?? renderEmailHtml(pvDesign as EmailDesign, { lang: pvLang }))} sandbox="" style={{ width: "100%", height: 380, border: `1px solid ${T.border}`, borderRadius: 8, background: T.white, display: "block" }} />
                  <div style={{ fontSize: 11, color: T.soft, marginTop: 8 }}>{pvServer
                    ? t("messaging.messageComposer.serverRendered", "Rendered by the platform — the same renderer the send uses.")
                    : pvServerFailed
                      ? t("messaging.messageComposer.serverRenderUnavailable", "Shown from this browser — the platform's render is unavailable, so this is not proof of what will send.")
                      : savedId
                        ? t("messaging.messageComposer.openDesignerForClients", "Open the designer's Preview for Gmail / Outlook / dark-mode / plain-text checks.")
                        : t("messaging.messageComposer.serverRenderNeedsSave", "Shown from this browser until the message is saved — the platform renders it after that.")}</div>
                </>
              ) : pvCh === "email" && pvDesign ? (
                /* A platform block document with no legacy `theme`: the browser
                   renderer cannot draw it (and crashes trying — the deployed
                   composer's `theme.bg` failure). Stated, not guessed. */
                <div style={{ padding: 18, border: `1px dashed ${T.n4}`, borderRadius: 10, background: T.n6, color: T.n2, fontSize: 12.5, lineHeight: 1.5, display: "flex", gap: 8 }}>
                  <span style={{ color: T.orange1, flexShrink: 0, display: "inline-flex", marginTop: 1 }}><Icon.info size={14} /></span>
                  <span>{savedId
                    ? t("messaging.messageComposer.platformDesignNoLocalPreview", "This email is a platform design — only the platform can render it, and its render is unavailable right now. Retry in a moment; nothing is wrong with the content.")
                    : t("messaging.messageComposer.platformDesignNeedsSave", "This email is a platform design — save the message and the platform renders the preview.")}</span>
                </div>
              ) : pvCh === "sms" ? (
                <div style={{ background: "#F2F2F7", borderRadius: 12, padding: 14 }}>
                  <div style={{ maxWidth: 280, background: "#E9E9EB", color: "#111114", borderRadius: "16px 16px 16px 4px", padding: "9px 12px", fontSize: 13.5, lineHeight: 1.45, whiteSpace: "pre-wrap" }}>{shown(pvBody) || <span style={{ color: T.soft }}>{t("messaging.messageComposer.yourSmsWillAppearHere", "Your SMS will appear here.")}</span>}</div>
                  <div style={{ fontSize: 10.5, color: T.soft, marginTop: 8, fontFamily: T.fontMono }}>{(() => { const s = smsSegments(pvBody); const enc = s.encoding === "ucs2" ? "UCS-2" : "GSM-7"; return s.estimated ? t("messaging.messageComposer.aboutNSegmentsChars", "~{c} chars · about {n} segment(s)").replace("{c}", String(s.chars)).replace("{n}", String(s.segments)) + ` · ${enc}` : `${s.chars}/${SMS_MAX} · ${s.segments} · ${enc}`; })()}</div>
                </div>
              ) : (
                <>
                  {pvSubj ? <div style={{ fontSize: 14, fontWeight: 700, color: T.n1, marginBottom: 6 }}>{pvSubj}</div> : null}
                  <div style={{ fontSize: 13.5, color: T.n1, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{pvBody || <span style={{ color: T.soft }}>{t("messaging.messageComposer.your", "Your")}{" "}{pvC.name} {t("messaging.messageComposer.contentWillAppearHere", "content will appear here.")}</span>}</div>
                </>
              )}
            </div>
          </Card>
          <div style={{ padding: 12, background: T.p4, border: `1px solid ${T.p3}`, borderRadius: 8, fontSize: 12.5, color: T.n1, lineHeight: 1.5, display: "flex", gap: 8 }}>
            <span style={{ color: T.p1, flexShrink: 0 }}><Icon.info size={14} /></span>
            <span>{channelNote(pvCh)}</span>
          </div>
        </div>
      </div>
      {flowOpen ? <AddToFlowModal message={form} onClose={() => setFlowOpen(false)} /> : null}
    </Screen>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   AUTOMATIC-MESSAGE SMS CONTENT (2026-09-16)

   The composer shell — same Screen, same PageHeader, same two-pane editor and
   live SMS preview, the same `TokenField` and the same `SmsBudget` — on ONE
   automatic message's SMS text.

   What it deliberately does NOT carry, and why each is absent rather than
   disabled: channel toggles (this row IS the SMS variant; the switches live on
   the overview), language tabs (appointment lineage holds one row per locale
   upstream), Make Live / Back to Draft / Add to Flow (the lineage has no such
   lifecycle, and the docket is explicit that a publish action must not be shown
   for a lineage that does not support it), and autosave (a content edit is an
   explicit save here, so nothing is written while the operator is still typing).

   Placeholders are scoped to what this message can resolve: person + its own
   family + organisation. The picker's own "Not available here" section then
   names the rest and says why, so an appointment message offers no queue or
   event tokens and explains their absence.
   ──────────────────────────────────────────────────────────────────────────── */
export function AutomaticSmsComposer({ id }: { id: string }) {
  const t = useT();
  const nav = useNav();
  const canRead = can("campaigns.read");
  const canWrite = can("campaigns.write");

  type Load =
    | { phase: "loading" }
    | { phase: "ready"; row: StudioMessage }
    | { phase: "missing" }
    | { phase: "error"; message: string }
    | { phase: "denied" };

  const [load, setLoad] = useState<Load>(() => (canRead ? { phase: "loading" } : { phase: "denied" }));
  const [text, setText] = useState("");
  const [stored, setStored] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /* THE CONSTRAINT IS THE PANE, NOT THE WINDOW. The preview sits beside the
     editor only when the pane itself can hold both. Measuring `window.innerWidth`
     was wrong by the width of the app shell's rail (~216px), so a 924px window
     with a 708px pane still went two-up and left the editor — the primary
     surface — narrower than the preview. Measured here with a ResizeObserver on
     the wrapper, so it is right whatever else is on screen. */
  const paneRef = useRef<HTMLDivElement | null>(null);
  const [wide, setWide] = useState(false);
  useEffect(() => {
    const el = paneRef.current;
    if (!el) return;
    const apply = () => setWide(el.getBoundingClientRect().width >= 720);
    apply();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", apply);
      return () => window.removeEventListener("resize", apply);
    }
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [load.phase]);

  const read = useCallback(() => {
    if (!canRead) { setLoad({ phase: "denied" }); return; }
    setLoad({ phase: "loading" });
    findSmsRow(id)
      .then((row) => {
        if (!row) { setLoad({ phase: "missing" }); return; }
        const body = smsTextOf(row);
        setText(body);
        setStored(body);
        setSaveError(null);
        setLoad({ phase: "ready", row });
      })
      .catch((e: unknown) => setLoad({ phase: "error", message: e instanceof Error ? e.message : String(e) }));
  }, [canRead, id]);

  useEffect(() => { read(); }, [read]);

  const row = load.phase === "ready" ? load.row : null;
  const ctx = smsTokenContext(row);
  /* The same projection the overview renders beside the switch, so the name and
     timing here are the ones the operator clicked — including the offset, which
     is what distinguishes the three reminders from each other. */
  const projected = row ? toMessageRow(row, "sms") : null;
  const outOfScope = outOfScopeTokens(text, ctx);
  const dirty = text !== stored;
  const tooLong = text.length > SMS_MAX;
  const empty = !text.trim();
  const preview = smsSegments(text);

  const back = () => nav.go("messaging.automatic");

  const save = () => {
    if (!canWrite || saving || tooLong || empty) return;
    setSaving(true);
    setSaveError(null);
    const sent = text;
    saveSmsText(id, sent)
      .then((server) => {
        setLoad({ phase: "ready", row: server });
        const body = smsTextOf(server);
        setStored(body);
        setText(body);
        toast({ tone: "success", title: t("messaging.sms.saved", "SMS text saved") });
      })
      .catch((e: unknown) => {
        // The input stays exactly as typed on every failure — a rejected save
        // must never cost the operator their text.
        setText(sent);
        setSaveError(e instanceof SmsNotPersistedError
          ? t("messaging.sms.notPersisted", "The server accepted the change but did not store the SMS text. Nothing was saved.")
          : e instanceof Error ? e.message : String(e));
      })
      .finally(() => setSaving(false));
  };

  return (
    <Screen rail="messaging">
      <PageHeader
        breadcrumb={(
          <span style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }} onClick={back}>
            <Icon.arrow dir="left" size={11} /> {t("messaging.tabs.automatic", "Automatic messages")}
          </span>
        )}
        title={projected ? projected.name : t("messaging.sms.title", "SMS text")}
        subtitle={(
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5, color: T.n2, paddingBottom: 6 }}>
            <Pill tone="primary">{t("messaging.auto.channel.sms", "SMS")}</Pill>
            {projected ? <span>{timingLine(projected, t)}</span> : null}
          </span>
        )}
        actions={row ? (
          <>
            <Btn variant="secondary" onClick={back}>{t("common.actions.close", "Close")}</Btn>
            <Btn disabled={!canWrite || !dirty || tooLong || empty || saving} onClick={save}>
              {saving ? t("messaging.sms.saving", "Saving…") : t("common.actions.save", "Save")}
            </Btn>
          </>
        ) : null}
      />
      {load.phase === "ready" ? (
        <div ref={paneRef} style={{ flex: 1, display: "grid", gridTemplateColumns: wide ? "minmax(380px,1fr) minmax(260px,340px)" : "minmax(0,1fr)", minHeight: 0, marginTop: 10, borderTop: `1px solid ${T.border}`, overflowX: "hidden", overflowY: wide ? "hidden" : "auto" }}>
          <div style={{ borderRight: wide ? `1px solid ${T.border}` : "none", overflow: wide ? "auto" : "visible", padding: "20px 24px 40px", background: T.canvas, minWidth: 0 }}>
            {/* What the message is for sits ABOVE the card (operator, 2026-09-16):
                it describes the message, not the field, and inside the card it read
                as part of the editor. */}
            {projected && purposeLine(projected, t) ? (
              <div style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.5, textWrap: "pretty", marginBottom: 12, maxWidth: 560 }}>
                {purposeLine(projected, t)}
              </div>
            ) : null}
            <Card style={{ padding: "16px 18px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <div style={msgLabel}>{t("messaging.sms.label", "SMS text")}<Req /></div>
                <TokenField
                  value={text}
                  onChange={setText}
                  ctx={ctx}
                  multiline
                  rows={5}
                  maxLength={SMS_MAX}
                  placeholder={t("messaging.sms.placeholder", "Hi {{first_name}}, your appointment is on {{date}} at {{time}}.")}
                  ariaRequired
                  invalid={empty || tooLong}
                  quick={ctx === "appointment" ? ["first_name", "service", "time", "location", "manage_short_url"]
                    : ctx === "queue" ? ["first_name", "queue_name", "position", "est_wait"]
                      : ctx === "event" ? ["first_name", "event_name", "event_date", "venue"]
                        : ["first_name", "org_name"]}
                  testid="sms-text"
                />
                <FieldError show={empty}>{t("messaging.sms.required", "SMS text is required — an empty template saves nothing.")}</FieldError>
                <FieldError show={tooLong}>
                  {t("messaging.sms.tooLong", "Over the {max} character limit the send path carries — shorten it before saving.").replace("{max}", String(SMS_MAX))}
                </FieldError>
              </div>
              <SmsBudget body={text} onFix={canWrite ? (v) => setText(v) : undefined} />
              {dirty ? (
                <div style={{ fontSize: 11.5, color: T.soft }}>{t("messaging.sms.unsaved", "Unsaved changes")}</div>
              ) : null}
              {!canWrite ? (
                <div style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.45 }}>
                  {t("messaging.auto.noWrite", "You do not have permission to change this ({capability}).")
                    .replace("{capability}", "campaigns.write")}
                </div>
              ) : null}
              {outOfScope.length ? (
                <div
                  role="alert"
                  style={{
                    padding: "8px 10px", borderRadius: 8, background: alpha(T.red1, 0.07),
                    border: `1px solid ${alpha(T.red1, 0.24)}`, fontSize: 12, color: T.red1, lineHeight: 1.45,
                  }}
                >
                  {t("messaging.sms.outOfScope", "These placeholders can't resolve in this message and would send as nothing:")}{" "}
                  {outOfScope.map((tk) => `{{${tk}}}`).join(", ")}
                </div>
              ) : null}
              {saveError ? (
                <div
                  role="status"
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 10px", borderRadius: 8,
                    background: alpha(T.red1, 0.07), border: `1px solid ${alpha(T.red1, 0.24)}`,
                    fontSize: 12, color: T.red1, lineHeight: 1.45,
                  }}
                >
                  <span style={{ flex: 1 }}>{t("messaging.sms.saveFailed", "Couldn't save.")} {saveError}</span>
                  <Btn size="sm" variant="secondary" onClick={save}>{t("messaging.auto.retry", "Retry")}</Btn>
                </div>
              ) : null}
            </Card>
            <div style={{ fontSize: 11.5, color: T.soft, lineHeight: 1.5, marginTop: 12 }}>
              {t("messaging.sms.scopeNote", "Saving changes this message's SMS text only — not its email content, not its timing, and not whether it is switched on.")}
            </div>
          </div>
          <div style={{ overflow: wide ? "auto" : "visible", padding: "20px 20px 40px", display: "flex", flexDirection: "column", gap: 12, minWidth: 0, borderTop: wide ? "none" : `1px solid ${T.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              {t("messaging.messageComposer.preview", "Preview")}
            </div>
            <Card style={{ padding: 14 }}>
              <div style={{ background: "#F2F2F7", borderRadius: 12, padding: 14 }}>
                <div style={{ maxWidth: 280, background: "#E9E9EB", color: "#111114", borderRadius: "16px 16px 16px 4px", padding: "9px 12px", fontSize: 13.5, lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
                  {resolveSamples(text) || <span style={{ color: T.soft }}>{t("messaging.messageComposer.yourSmsWillAppearHere", "Your SMS will appear here.")}</span>}
                </div>
                <div style={{ fontSize: 10.5, color: T.soft, marginTop: 8, fontFamily: T.fontMono }}>
                  {`${preview.estimated ? "~" : ""}${preview.chars}/${SMS_MAX} · ${preview.segments} · ${preview.encoding === "ucs2" ? "UCS-2" : "GSM-7"}`}
                </div>
              </div>
            </Card>
            <div style={{ padding: 12, background: T.p4, border: `1px solid ${T.p3}`, borderRadius: 8, fontSize: 12.5, color: T.n1, lineHeight: 1.5, display: "flex", gap: 8 }}>
              <span style={{ color: T.p1, flexShrink: 0 }}><Icon.info size={14} /></span>
              <span>{t("messaging.sms.previewNote", "Sample values, filled in locally. The real message resolves each placeholder per recipient when it is sent.")}</span>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, overflow: "auto", padding: "16px 24px 28px", maxWidth: 680 }}>
          {load.phase === "denied" ? (
            <Card style={{ padding: "16px 18px" }}>
              <div style={{ fontSize: 13, color: T.n1, fontWeight: 650, marginBottom: 4 }}>
                {t("messaging.auto.deniedTitle", "You don't have access to automatic messages")}
              </div>
              <div style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.5 }}>
                {t("messaging.auto.deniedBody", "Viewing these messages needs the campaigns.read permission. Ask an administrator to grant it.")}
              </div>
            </Card>
          ) : load.phase === "loading" ? (
            <div style={{ fontSize: 13, color: T.soft }}>{t("messaging.sms.loading", "Loading the SMS text…")}</div>
          ) : load.phase === "missing" ? (
            /* NOT permission to edit the email row, and not a redirect to a
               template list: the SMS record for this message is not served, and
               that is what the operator is told. */
            <Card style={{ padding: "16px 18px" }}>
              <div style={{ fontSize: 13, color: T.n1, fontWeight: 650, marginBottom: 4 }}>
                {t("messaging.sms.missingTitle", "No SMS template for this message yet")}
              </div>
              <div style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.5 }}>
                {t("messaging.sms.missingBody", "The catalogue serves no SMS record for it, so there is nothing to edit here. Its email content is unaffected.")}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <Btn size="sm" variant="secondary" onClick={read}>{t("messaging.auto.retry", "Retry")}</Btn>
                <Btn size="sm" variant="secondary" onClick={back}>{t("common.actions.close", "Close")}</Btn>
              </div>
            </Card>
          ) : (
            <div
              style={{
                display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderRadius: 10,
                background: T.dangerSurface, border: `1px solid ${T.dangerSurfaceEdge}`,
                color: T.dangerSurfaceInk, fontSize: 13,
              }}
            >
              <span style={{ flex: 1 }}>
                {t("messaging.sms.loadFailed", "Couldn't load this message.")} {load.phase === "error" ? load.message : ""}
              </span>
              <Btn size="sm" variant="secondary" onClick={read}>{t("messaging.auto.retry", "Retry")}</Btn>
            </div>
          )}
        </div>
      )}
    </Screen>
  );
}

/* ── Add-to-Flow picker ─────────────────────────────────────────────────
 * Insert this message as a comms step into an existing flow, choosing which
 * flow and the position in its top-level sequence. Branch-level placement is
 * left to the flow builder. */
function flowStepLabel(s: FBStep, name: (id: string | null) => string): string {
  switch (s.kind) {
    case "comms": return name(s.messageId);
    case "wait": return fbWaitLabel(s);
    case "decision": return `Decision · ${s.condition}`;
    case "exit": return `Exit · ${s.outcome}`;
    case "landing": return "Landing page";
    default: return "Step";
  }
}

export function AddToFlowModal({ message, onClose }: { message: MessageItem; onClose: () => void }) {
  const t = useT();
  const flows = useFlows();
  const msgs = useMessaging();
  const [selId, setSelId] = useState<string | null>(null);
  const [pos, setPos] = useState(0);
  const list = flows.all();
  const sel = selId ? flows.get(selId) : null;
  const name = (id: string | null) => (id ? (msgs.get(id)?.name || "Message") : "Message");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const pick = (id: string) => { setSelId(id); const f = flows.get(id); setPos(f ? f.steps.length : 0); };
  const add = async () => {
    if (!sel) return;
    const steps = fbInsert(sel.steps, { parentId: null, pathKey: null, index: pos }, fbMkComms(message.id));
    try {
      await flows.upsert({ ...sel, steps });
      toast({ tone: "success", title: t("messaging.messageComposer.addedToFlow", "Added to flow"), subtitle: i18nT("messaging.messageComposer.messageNameMessageAddedToSelName.icu", { value0: message.name || "Message", value1: sel.name }) });
      onClose();
    } catch (e) {
      console.warn("add-to-flow failed:", e);
      toast({ tone: "danger", title: t("messaging.messageComposer.couldnTAddToFlow", "Couldn't add to flow"), subtitle: t("messaging.messageComposer.theFlowIsUnchangedRetryInAMoment", "The flow is unchanged. Retry in a moment.") });
    }
  };

  return (
    <div onClick={onClose} role="dialog" aria-label={t("messaging.messageComposer.addMessageToAFlow", "Add message to a flow")} style={{ position: "fixed", inset: 0, zIndex: 240, background: "rgba(20,16,46,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 560, maxHeight: "82vh", background: T.white, borderRadius: 14, boxShadow: T.shadowLg, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: T.n1, margin: 0 }}>{t("messaging.messageComposer.addToAFlow", "Add to a flow")}</h2>
            <p style={{ fontSize: 12.5, color: T.soft, margin: "3px 0 0" }}>{t("messaging.messageComposer.insert", "Insert")}{" "}<b style={{ color: T.n2 }}>{message.name || "this message"}</b> {t("messaging.messageComposer.asAStepInAnExistingFlow", "as a step in an existing flow.")}</p>
          </div>
          <IconBtn size={32} aria-label={t("common.actions.close", "Close")} onClick={onClose}><Icon.x size={16} /></IconBtn>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: "14px 22px 6px", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: 0.5 }}>{t("messaging.messageComposer.chooseAFlow", "Choose a flow")}</div>
          {list.length === 0 ? <div style={{ fontSize: 13, color: T.soft, padding: "10px 2px" }}>{t("messaging.messageComposer.noFlowsYetCreateOneInMessagingFlowsFirst", "No flows yet. Create one in Messaging → Flows first.")}</div> : null}
          {list.map((f) => {
            const on = selId === f.id;
            const st = flowStats(f);
            const pill = FLOW_KIND_PILL[f.eventKind] || { label: f.eventKind, tone: "primary" as const };
            return (
              /* ui-raw: bordered selectable choice card — ChoiceTile/SelectableTile's pattern (group 10) */
              <button key={f.id} onClick={() => pick(f.id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 13px", borderRadius: 10, border: `1.5px solid ${on ? T.p1 : T.border}`, background: on ? T.p4 : T.white, cursor: "pointer", textAlign: "left", fontFamily: T.font }}>
                <span style={{ width: 18, height: 18, borderRadius: 999, border: `2px solid ${on ? T.p1 : T.n4}`, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{on ? <span style={{ width: 8, height: 8, borderRadius: 999, background: T.p1 }} /> : null}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n1 }}>{f.name}</div>
                  <div style={{ fontSize: 11.5, color: T.soft, marginTop: 1 }}>{t("messaging.messageComposer.messageAndStepCounts.icu", { messageCount: st.messages, stepCount: st.steps })}</div>
                </div>
                {pill ? <Pill tone={pill.tone}>{pill.label}</Pill> : null}
              </button>
            );
          })}
        </div>
        {sel ? (
          <div style={{ padding: "12px 22px", borderTop: `1px solid ${T.border}`, background: T.canvas }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.n1, marginBottom: 6 }}>{t("messaging.messageComposer.positionIn", "Position in “")}{sel.name}”</div>
            <div style={{ position: "relative" }}>
              <select value={pos} onChange={(e) => setPos(Number(e.target.value))} style={msgInput({ appearance: "none", WebkitAppearance: "none", paddingRight: 34, cursor: "pointer" })}>
                <option value={0}>{t("messaging.messageComposer.atTheStart", "At the start")}</option>
                {sel.steps.map((s, i) => <option key={s.id} value={i + 1}>{t("messaging.messageComposer.after", "After:")}{" "}{flowStepLabel(s, name)}</option>)}
              </select>
              <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: T.n2 }}>{CARET}</span>
            </div>
            <div style={{ fontSize: 11.5, color: T.soft, marginTop: 6 }}>{t("messaging.messageComposer.onlyTheTopLevelOfTheFlowIsOfferedHereFineTune", "Only the top level of the flow is offered here — fine-tune branch placement later in the flow builder.")}</div>
          </div>
        ) : null}
        <div style={{ padding: "14px 22px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Btn variant="secondary" onClick={onClose}>{t("common.actions.cancel", "Cancel")}</Btn>
          <Btn icon={<Icon.plus size={14} />} disabled={!sel} onClick={add}>{t("messaging.messageComposer.addToFlowd264521e", "Add to flow")}</Btn>
        </div>
      </div>
    </div>
  );
}

/* ── SMS budget indicator ─────────────────────────────────────────────────────
 * Segments, not characters, are what gets billed — so the segment count is the
 * primary number and the character count sits beside it as context. The encoding
 * is named because it is the thing that MOVES the budget: one character outside
 * GSM-7 drops the first segment from 160 to 70, and the character that usually
 * does it (a typographic quote pasted in from a document) is invisible next to a
 * straight one. Counting happens on the RESOLVED text, so switching
 * {{appointment.manage_url}} → {{appointment.manage_short_url}} moves the number. */
function SmsBudget({ body, onFix }: { body: string; onFix?: (next: string) => void }) {
  const t = useT();
  const s = smsSegments(body);
  /* WHOSE CHARACTER IS IT (2026-09-16, operator: "I don't understand the em dash
     problem"). The budget counts the RESOLVED text, which is right — a token's
     sample stands in for the real value's length. But the offender list then
     mixed two very different findings: a character the operator TYPED, which
     they can change, and one that arrived inside a token's sample value
     ("Boston — Back Bay"), which they cannot — and the warning asked them to fix
     it anyway, offering a rewrite that would touch nothing. So the authored text
     is analysed separately and the two cases are worded separately; only typed
     characters are offered the rewrite. */
  const authored = analyzeSms(body);
  const typed = authored.offenders;
  const ucs2 = s.encoding === "ucs2";
  const fromSampleOnly = ucs2 && typed.length === 0;
  const fixable = typed.length > 0 && allFixable(typed) && !!onFix;
  const tone = ucs2 ? "#B45309" : s.segments > 1 ? T.n2 : T.soft;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="sms-budget">
      {/* Wraps rather than being cut: at a narrow pane the readout was clipped
          by ~38px with the encoding and the remaining count off the end — the
          two facts that make the number mean anything. */}
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, rowGap: 4, flexWrap: "wrap", fontSize: 11.5, color: T.soft }}>
        <span>{t('messaging.messageComposer.text8')}</span>
        <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6, rowGap: 2, flexWrap: "wrap", color: tone }}>
          <b style={{ fontSize: 12.5, color: s.segments > 1 ? T.n1 : tone }}>
            {s.segments} {s.segments === 1 ? t("messaging.messageComposer.segment", "segment") : t("messaging.messageComposer.segments", "segments")}
          </b>
          <span aria-hidden="true">·</span>
          <span>{s.estimated ? `~${s.chars}` : s.chars} {t("messaging.messageComposer.chars", "chars")}</span>
          <span aria-hidden="true">·</span>
          <span style={{ fontFamily: T.fontMono, fontSize: 11 }}>{ucs2 ? "UCS-2" : "GSM-7"}</span>
          {s.segments > 0 ? <><span aria-hidden="true">·</span><span>{t("messaging.messageComposer.leftInSegment", "{n} left").replace("{n}", String(s.remaining))}</span></> : null}
        </span>
      </div>
      {ucs2 ? (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, rowGap: 6, flexWrap: "wrap", padding: "8px 10px", borderRadius: 8, background: alpha("#B45309", 0.07), border: `1px solid ${alpha("#B45309", 0.24)}`, fontSize: 11.5, color: "#7C3E06", lineHeight: 1.5 }}>
          <Icon.warn size={13} style={{ flexShrink: 0, marginTop: 1, color: "#B45309" }} />
          <span style={{ flex: "1 1 200px", minWidth: 0 }}>
            {fromSampleOnly
              ? t("messaging.messageComposer.smsUcs2FromSample", "A placeholder's sample value contains {chars}, so this estimate uses Unicode encoding — 70 characters per segment instead of 160. Your own text is fine; whether a real send is Unicode depends on the value that fills in.")
                .replace("{chars}", s.offenders.slice(0, 3).map(describeChar).join(", "))
              : t("messaging.messageComposer.smsUcs2Warning", "{chars} forces Unicode encoding, which cuts every segment from 160 characters to 70.")
                .replace("{chars}", typed.slice(0, 3).map(describeChar).join(", "))}
            {!fromSampleOnly && typed.length > 3 ? ` (+${typed.length - 3})` : ""}
          </span>
          {fixable ? (
            <Btn size="sm" variant="secondary" onClick={() => onFix?.(toGsmSafe(body))}>
              {t("messaging.messageComposer.smsUcs2Fix", "Use plain equivalents")}
            </Btn>
          ) : null}
        </div>
      ) : null}
      {s.estimated ? (
        <div style={{ fontSize: 11, color: T.soft }}>{t("messaging.messageComposer.smsEstimated", "Counted with sample values — a token's real length decides the final count.")}</div>
      ) : null}
    </div>
  );
}

/* ── Per-channel notice shown under the preview ──────────────────────── */
function channelNote(active: string): string {
  switch (active) {
    case "whatsapp": return "WhatsApp messages sent outside the 24-hour service window must be templates pre-approved by Meta — and Meta approves each language separately, so every language version carries its own review status.";
    case "sms": return "SMS is plain text, ~160 characters per segment. Personalisation tokens like {first_name} are supported.";
    case "calendar": return "A calendar invite lands in the customer's own calendar. An Update reuses the same event id so it MOVES the existing entry rather than adding a second one — the usual way this feature goes wrong.";
    case "wallet": return "A wallet pass isn't sent once — it's a card on the customer's lock screen that keeps updating itself: instantly on a reschedule or cancellation, continuously for a live queue position. Producing an installable pass needs real Apple/Google signing infrastructure (REQ-117); this is the authoring view and preview.";
    default: return "Email supports rich HTML content and personalisation tokens like {first_name}.";
  }
}

/* ── WhatsApp structured template editor ─────────────────────────────────── */
function IcsEditor({ ics, onIcs, ctx }: { ics: IcsTemplate; onIcs: (p: Partial<IcsTemplate>) => void; ctx: ReturnType<typeof contextOf> }) {
  const t = useT();
  const m = ICS_METHODS.find((x) => x.id === ics.method) || ICS_METHODS[0];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <NotStoredNote what="The calendar invite is not stored on the message yet — it becomes a property of the appointment lineage, so this content does not survive a reload today." ticket="JAI-1386" />
      <div>
        <div style={msgLabel}>{t("messaging.messageComposer.icsMethod", "What this invite does")}<Req /></div>
        <div style={{ display: "flex", gap: 6 }}>
          {ICS_METHODS.map((x) => {
            const on = ics.method === x.id;
            return (
              /* ui-raw: bordered selectable choice card — ChoiceTile/SelectableTile's pattern (group 10) */
              <button key={x.id} type="button" onClick={() => onIcs({ method: x.id })} data-testid={`mcomposer-ics-${x.id}`}
                style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: `1.5px solid ${on ? T.blue1 : T.border}`, background: on ? T.blue2 : T.white, color: on ? T.blue1 : T.n1, fontFamily: T.font, fontSize: 12.5, fontWeight: on ? 700 : 500, cursor: "pointer" }}>{t(x.labelKey, x.label)}</button>
            );
          })}
        </div>
        <div style={{ fontSize: 11.5, color: T.soft, marginTop: 6, lineHeight: 1.5 }}>{m.help}</div>
      </div>
      <div><div style={msgLabel}>{t("messaging.messageComposer.icsSummary", "Calendar entry title")}<Req /></div>
        <TokenField value={ics.summary} onChange={(v) => onIcs({ summary: v })} ctx={ctx} quick={["service", "staff", "event_name"]} ariaRequired invalid={!ics.summary.trim()} testid="mcomposer-ics-summary" />
        <FieldError show={!ics.summary.trim()}>{t("messaging.messageComposer.icsSummaryRequired", "The calendar entry needs a title — it is what the customer sees in their calendar.")}</FieldError>
      </div>
      <div><div style={msgLabel}>{t("messaging.messageComposer.icsDescription", "Notes in the calendar entry")}</div>
        <TokenField value={ics.description} onChange={(v) => onIcs({ description: v })} ctx={ctx} multiline rows={4} quick={["manage_url", "directions_url"]} testid="mcomposer-ics-desc" />
      </div>
      <div><div style={msgLabel}>{t("messaging.messageComposer.icsLocation", "Location")}</div>
        <TokenField value={ics.location} onChange={(v) => onIcs({ location: v })} ctx={ctx} quick={["location_address", "venue_address", "join_url"]} testid="mcomposer-ics-loc" />
        <div style={{ fontSize: 11, color: T.soft, marginTop: 5 }}>{t("messaging.messageComposer.icsLocationHint", "A full address lets their calendar offer directions. For a virtual appointment, use the join link instead.")}</div>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1 }}><div style={msgLabel}>{t("messaging.messageComposer.icsReminder", "Their calendar reminder")}</div>
          <div style={{ position: "relative" }}>
            <select value={String(ics.reminderMins)} onChange={(e) => onIcs({ reminderMins: Number(e.target.value) })} style={msgInput({ appearance: "none", WebkitAppearance: "none", paddingRight: 34, cursor: "pointer" })}>
              <option value="0">{t("messaging.messageComposer.icsNoReminder", "No reminder")}</option>
              <option value="15">15 {t("messaging.messageComposer.minsBefore", "minutes before")}</option>
              <option value="60">1 {t("messaging.messageComposer.hourBefore", "hour before")}</option>
              <option value="1440">1 {t("messaging.messageComposer.dayBefore", "day before")}</option>
            </select>
            <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: T.n2 }}>{CARET}</span>
          </div>
        </div>
        <div style={{ flex: 1 }}><div style={msgLabel}>{t("messaging.messageComposer.icsOrganiser", "Organiser name")}</div>
          <input value={ics.organiserName} onChange={(e) => onIcs({ organiserName: e.target.value })} style={msgInput()} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, padding: "10px 12px", background: T.blue2, border: `1px solid ${alpha(T.blue1, "33")}`, borderRadius: 8, fontSize: 11.5, color: T.n1, lineHeight: 1.5 }}>
        <Icon.info size={14} style={{ color: T.blue1, flexShrink: 0, marginTop: 1 }} />
        <span>{t("messaging.messageComposer.icsUidNote", "The event id comes from the booking, not from this message — that is what lets an Update move the entry the customer already has instead of creating a duplicate.")}</span>
      </div>
    </div>
  );
}

function IcsPreview({ ics, raw }: { ics: IcsTemplate; raw: boolean }) {
  const t = useT();
  const text = buildIcs(ics, { resolve: raw ? undefined : resolveSamples });
  const m = ICS_METHODS.find((x) => x.id === ics.method) || ICS_METHODS[0];
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Pill tone={ics.method === "cancel" ? "danger" : "info"}>{t(m.labelKey, m.label)}</Pill>
        <span style={{ fontSize: 11.5, color: T.soft, fontFamily: T.fontMono }}>invite.ics</span>
      </div>
      <div style={{ border: `1px solid ${T.border}`, borderRadius: 9, overflow: "hidden", marginBottom: 10 }}>
        <div style={{ height: 4, background: ics.method === "cancel" ? T.red1 : T.blue1 }} />
        <div style={{ padding: "11px 13px" }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: T.n1, textDecoration: ics.method === "cancel" ? "line-through" : "none" }}>{raw ? ics.summary : resolveSamples(ics.summary)}</div>
          <div style={{ fontSize: 12, color: T.soft, marginTop: 3 }}>{t("messaging.messageComposer.icsSampleWhen", "Tue 14 Oct · 14:30–15:15")}</div>
          <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{raw ? ics.location : resolveSamples(ics.location)}</div>
        </div>
      </div>
      <pre style={{ margin: 0, padding: 12, background: "#14102E", color: "#DCCCFC", borderRadius: 9, fontFamily: T.fontMono, fontSize: 10.5, lineHeight: 1.6, whiteSpace: "pre-wrap", overflowX: "auto" }}>{text}</pre>
    </div>
  );
}

/* ── Wallet-pass editor ────────────────────────────────────────────────────
 * The pass SHAPE (ticket/appointment/queue) is fixed by the message; the
 * author edits its field content, not its layout. */
function WalletEditor({ tpl, onChange, type, ctx }: { tpl: WalletTemplate; onChange: (p: Partial<WalletTemplate>) => void; type: WalletPassType; ctx: ReturnType<typeof contextOf> }) {
  const t = useT();
  const setSecondary = (i: number, patch: Partial<WalletField>) => onChange({ secondary: tpl.secondary.map((f, j) => j === i ? { ...f, ...patch } : f) });
  const setBack = (i: number, patch: Partial<WalletField>) => onChange({ back: tpl.back.map((f, j) => j === i ? { ...f, ...patch } : f) });
  const addBack = () => tpl.back.length >= 4 ? undefined : onChange({ back: [...tpl.back, { label: "", value: "" }] });
  const rmBack = (i: number) => onChange({ back: tpl.back.filter((_, j) => j !== i) });
  const fieldRow = (label: string, val: WalletField, onLabel: (v: string) => void, onVal: (v: string) => void, quick: string[], testid: string) => (
    <div style={{ display: "flex", gap: 8 }}>
      <input value={val.label} onChange={(e) => onLabel(e.target.value)} placeholder={label} data-testid={`${testid}-label`}
        style={msgInput({ width: 110, flexShrink: 0, fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.3 })} />
      <div style={{ flex: 1 }}><TokenField value={val.value} onChange={onVal} ctx={ctx} quick={quick} testid={testid} /></div>
    </div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <NotStoredNote what="The wallet pass is not stored on the message yet — it becomes a property of the appointment lineage, so this content does not survive a reload today." ticket="JAI-1386" />
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", background: T.p4, border: `1px solid ${T.p3}`, borderRadius: 8, fontSize: 12.5, color: T.n1 }}>
        <Icon.info size={14} style={{ color: T.p1, flexShrink: 0 }} />
        <span><b>{WALLET_TYPE_LABEL[type]}</b> — {WALLET_UPDATE_TRIGGER[type]}</span>
      </div>
      <div>
        <div style={msgLabel}>{t("messaging.messageComposer.walletPrimary", "Primary field")}</div>
        {fieldRow("LABEL", { label: tpl.primaryLabel, value: tpl.primaryValue }, (v) => onChange({ primaryLabel: v }), (v) => onChange({ primaryValue: v }), ["event_name", "service", "queue_name"], "mcomposer-wallet-primary")}
      </div>
      <div>
        <div style={msgLabel}>{t("messaging.messageComposer.walletSecondary", "Secondary fields")}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {tpl.secondary.map((f, i) => <div key={i}>{fieldRow("LABEL", f, (v) => setSecondary(i, { label: v }), (v) => setSecondary(i, { value: v }), ["event_date", "date", "time", "position", "est_wait"], `mcomposer-wallet-sec-${i}`)}</div>)}
        </div>
      </div>
      <div>
        <div style={msgLabel}>{t("messaging.messageComposer.walletAux", "Auxiliary field")}</div>
        {fieldRow("LABEL", tpl.aux, (v) => onChange({ aux: { ...tpl.aux, label: v } }), (v) => onChange({ aux: { ...tpl.aux, value: v } }), ["staff", "venue", "location"], "mcomposer-wallet-aux")}
      </div>
      <div>
        <div style={msgLabel}>{t("messaging.messageComposer.walletBarcode", "Barcode / QR payload")}</div>
        <TokenField value={tpl.barcodeMessage} onChange={(v) => onChange({ barcodeMessage: v })} ctx={ctx} quick={["ticket_url", "manage_url"]} placeholder={t("messaging.messageComposer.walletBarcodeEmpty", "Leave empty for no barcode")} testid="mcomposer-wallet-barcode" />
      </div>
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <div style={{ ...msgLabel, marginBottom: 0 }}>{t("messaging.messageComposer.walletBack", "Back of pass")}</div>
          <Btn size="sm" variant="secondary" onClick={addBack} disabled={tpl.back.length >= 4}>{t("messaging.messageComposer.walletAddDetail", "+ Add detail")}</Btn>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {tpl.back.map((f, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{ flex: 1 }}>{fieldRow("LABEL", f, (v) => setBack(i, { label: v }), (v) => setBack(i, { value: v }), ["manage_url", "venue_address", "location_address", "rejoin_url"], `mcomposer-wallet-back-${i}`)}</div>
              <IconBtn size={32} type="button" onClick={() => rmBack(i)} aria-label={t("messaging.messageComposer.removeDetail", "Remove")}><Icon.x size={13} /></IconBtn>
            </div>
          ))}
        </div>
      </div>
      <Btn variant="secondary" disabled title={t("messaging.messageComposer.walletDownloadHeld", "Needs real pass signing (Apple Pass Type ID cert / Google Wallet issuer JWT) — REQ-117")}>{t("messaging.messageComposer.walletDownloadSample", "Download sample pass")}</Btn>
    </div>
  );
}

/* Wallet-pass front/back preview, styled after the two real platforms' card
 * shape without reproducing their marks — tenant-branded via useBranding(). */
function WalletPreview({ tpl, type, raw, branding }: { tpl: WalletTemplate; type: WalletPassType; raw: boolean; branding: { primary?: string; logoUrl?: string; name?: string } }) {
  const t = useT();
  const [platform, setPlatform] = useState<"apple" | "google">("apple");
  const [side, setSide] = useState<"front" | "back">("front");
  const show = (s: string) => (raw ? s : resolveSamples(s));
  const bg = branding.primary || "#1F2430";
  const radius = platform === "apple" ? 16 : 12;
  return (
    <div>
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {(["apple", "google"] as const).map((p) => (
          /* ui-raw: bordered selectable choice card — ChoiceTile/SelectableTile's pattern (group 10) */
          <button key={p} type="button" onClick={() => setPlatform(p)} aria-pressed={platform === p}
            style={{ flex: 1, padding: "7px 10px", borderRadius: 8, border: `1.5px solid ${platform === p ? T.p1 : T.border}`, background: platform === p ? T.p4 : T.white, color: platform === p ? T.p1 : T.n1, fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: T.font }}>
            {p === "apple" ? t("messaging.messageComposer.walletApple", "Apple Wallet") : t("messaging.messageComposer.walletGoogle", "Google Wallet")}
          </button>
        ))}
      </div>
      <div onClick={() => setSide(side === "front" ? "back" : "front")} title={t("messaging.messageComposer.walletFlip", "Click to flip")} style={{ borderRadius: radius, background: bg, color: "#fff", padding: 18, cursor: "pointer", boxShadow: "0 10px 28px -12px rgba(0,0,0,0.45)", minHeight: 168, position: "relative", overflow: "hidden" }}>
        {side === "front" ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              {branding.logoUrl ? <img src={branding.logoUrl} alt="" style={{ width: 22, height: 22, borderRadius: 5, objectFit: "cover" }} /> : <span style={{ width: 22, height: 22, borderRadius: 5, background: "rgba(255,255,255,0.25)" }} />}
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, opacity: 0.85, textTransform: "uppercase" }}>{branding.name || t("messaging.messageComposer.walletDefaultOrg", "Acme Live")}</span>
              {type === "queue" ? <span style={{ marginLeft: "auto", fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, padding: "2px 7px", borderRadius: 999, background: "rgba(255,255,255,0.2)" }}>{t("messaging.messageComposer.walletLive", "● LIVE")}</span> : null}
            </div>
            <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, opacity: 0.75, textTransform: "uppercase" }}>{tpl.primaryLabel}</div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2, marginBottom: 14, lineHeight: 1.25 }}>{show(tpl.primaryValue)}</div>
            <div style={{ display: "flex", gap: 22, marginBottom: 10 }}>
              {tpl.secondary.map((f, i) => (
                <div key={i}>
                  <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4, opacity: 0.7, textTransform: "uppercase" }}>{f.label}</div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, marginTop: 2 }}>{show(f.value)}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4, opacity: 0.7, textTransform: "uppercase" }}>{tpl.aux.label}</div>
            <div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 2 }}>{show(tpl.aux.value)}</div>
            {tpl.barcodeMessage ? (
              <div style={{ position: "absolute", right: 16, bottom: 16, width: 44, height: 44, borderRadius: 6, background: "rgba(255,255,255,0.92)", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gridTemplateRows: "repeat(4,1fr)", gap: 2, padding: 5 }}>
                {Array.from({ length: 16 }).map((_, i) => <div key={i} style={{ background: (i * 7) % 3 ? bg : "transparent", borderRadius: 1 }} />)}
              </div>
            ) : null}
          </>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {tpl.back.map((f, i) => (
              <div key={i}>
                <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.4, opacity: 0.7, textTransform: "uppercase" }}>{f.label}</div>
                <div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 2, wordBreak: "break-word" }}>{show(f.value)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ fontSize: 11, color: T.soft, marginTop: 8, textAlign: "center" }}>{side === "front" ? t("messaging.messageComposer.walletTapBack", "Tap the pass to see the back") : t("messaging.messageComposer.walletTapFront", "Tap the pass to see the front")}</div>
    </div>
  );
}

/* A field the author can fill that the platform does not store YET. Stated at
 * the point of authoring, per CC 2026-09-03: caching it silently is how "I typed
 * it and it vanished" happens. */
function NotStoredNote({ what, ticket }: { what: string; ticket: string }) {
  return (
    <div style={{ display: "flex", gap: 8, padding: "9px 12px", marginBottom: 12, background: T.orange2, border: `1px solid ${alpha(T.orange1, "40")}`, borderRadius: 8, fontSize: 11.5, color: T.n1, lineHeight: 1.45 }}>
      <span style={{ color: T.orange1, flexShrink: 0, display: "inline-flex", marginTop: 1 }}><Icon.warn size={13} /></span>
      <span><b>Not yet stored.</b> {what} <span style={{ fontFamily: T.fontMono, fontSize: 11 }}>{ticket}</span></span>
    </div>
  );
}

function WaEditor({ wa, body, onBody, setWa, addBtn, setBtn, rmBtn }: {
  wa: WaTemplate; body: string; onBody: (t: string) => void;
  setWa: (p: Partial<WaTemplate>) => void;
  addBtn: () => void; setBtn: (id: string, p: Partial<WaButton>) => void; rmBtn: (id: string) => void;
}) {
  const t = useT();
  const sp = WA_STATUS_PILL[wa.status];
  const header = WA_HEADERS.find((item) => item.id === wa.headerType);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <NotStoredNote what="The message text is saved. Category, header, footer and buttons are not stored by the platform yet, so they do not survive a reload." ticket="JAI-1385" />
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: T.n1 }}>{t("messaging.messageComposer.whatsappTemplate", "WhatsApp template")}</span>
        <Pill tone={sp.tone}>{t(sp.labelKey, sp.label)}</Pill>
        <span style={{ flex: 1 }} />
        {wa.status === "draft" ? <Btn variant="secondary" onClick={() => setWa({ status: "in_review" })}>{t("messaging.messageComposer.submitForReview", "Submit for review")}</Btn>
          : wa.status === "in_review" ? <span style={{ fontSize: 11.5, color: T.soft }}>{t("messaging.messageComposer.awaitingMetaApproval", "Awaiting Meta approval…")}</span>
            : wa.status === "rejected" ? <Btn variant="secondary" onClick={() => setWa({ status: "in_review" })}>{t("messaging.messageComposer.resubmit", "Resubmit")}</Btn>
              : null}
      </div>

      <div>
        <div style={msgLabel}>{t("messaging.messageComposer.category", "Category")}<Req /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          {WA_CATS.map((c) => {
            const on = wa.category === c.id;
            return (
              /* ui-raw: bordered selectable choice card — ChoiceTile/SelectableTile's pattern (group 10) */
              <button key={c.id} type="button" onClick={() => setWa({ category: c.id })} style={{ textAlign: "left", padding: "8px 10px", borderRadius: 8, border: `1.5px solid ${on ? WA_HUE : T.border}`, background: on ? WA_HUE + "12" : T.white, cursor: "pointer", fontFamily: T.font }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: on ? "#128C3E" : T.n1 }}>{t(c.labelKey, c.label)}</div>
                <div style={{ fontSize: 10.5, color: T.soft, marginTop: 2, lineHeight: 1.3 }}>{c.sub}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div style={msgLabel}>{t("messaging.messageComposer.header", "Header")}<span style={{ color: T.soft, fontWeight: 400 }}>{t("messaging.messageComposer.optional", "· optional")}</span></div>
        <div style={{ position: "relative" }}>
          <select value={wa.headerType} onChange={(e) => setWa({ headerType: e.target.value as WaHeaderType })} style={msgInput({ appearance: "none", WebkitAppearance: "none", paddingRight: 34, cursor: "pointer" })}>
            {WA_HEADERS.map((h) => <option key={h.id} value={h.id}>{t(h.labelKey, h.label)}</option>)}
          </select>
          <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: T.n2 }}>{CARET}</span>
        </div>
        {wa.headerType === "text" ? <input value={wa.headerText} onChange={(e) => setWa({ headerText: e.target.value })} placeholder={t("messaging.messageComposer.headerTextMax60", "Header text (max 60)")} maxLength={60} style={msgInput({ marginTop: 8 })} /> : null}
        {wa.headerType !== "none" && wa.headerType !== "text" ? <div style={{ marginTop: 8, padding: "10px 12px", border: `1px dashed ${T.n4}`, borderRadius: 8, fontSize: 12, color: T.soft, display: "flex", alignItems: "center", gap: 8 }}><Icon.info size={14} /> {header ? t(header.labelKey, header.label) : ""} {t("messaging.messageComposer.attachmentIsSetAtSendTime", "attachment is set at send time.")}</div> : null}
      </div>

      <div>
        <div style={msgLabel}>{t("messaging.messageComposer.body", "Body")}</div>
        <textarea value={body} onChange={(e) => onBody(e.target.value)} rows={6} placeholder={t("messaging.messageComposer.writeTheTemplateBody", "Write the template body…")} style={msgInput({ height: "auto", padding: 12, resize: "vertical", lineHeight: 1.5 })} />
        <div style={{ fontSize: 11, color: T.soft, marginTop: 5 }}>{t("messaging.messageComposer.waNamedTokens", "Write named tokens as you would anywhere else — they are converted to Meta's numbered placeholders when you submit for review.")}</div>
        {(() => {
          const c = compileForWhatsApp(body);
          if (!c.map.length) return null;
          return (
            <div style={{ marginTop: 9, padding: "10px 12px", background: T.n6, border: `1px solid ${T.border}`, borderRadius: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.soft, textTransform: "uppercase", letterSpacing: 0.04 }}>{t("messaging.messageComposer.metaMapping", "What Meta receives")}</div>
              <div style={{ fontFamily: T.fontMono, fontSize: 11.5, color: T.n1, margin: "6px 0 7px", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{c.body}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {c.map.map((r) => (
                  <div key={r.n} style={{ fontSize: 11, color: T.soft }}>
                    <span style={{ fontFamily: T.fontMono, color: T.n2 }}>{"{{" + r.n + "}}"}</span> = {(tokenDef(r.id) || { label: r.id }).label} <span style={{ color: T.n3 }}>· {r.sample}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>

      <div>
        <div style={msgLabel}>{t("messaging.messageComposer.footer", "Footer")}<span style={{ color: T.soft, fontWeight: 400 }}>{t("messaging.messageComposer.optional", "· optional")}</span></div>
        <input value={wa.footer} onChange={(e) => setWa({ footer: e.target.value })} placeholder={t("messaging.messageComposer.smallPrintEGReplyStopToOptOut", "Small print, e.g. Reply STOP to opt out")} maxLength={60} style={msgInput()} />
      </div>

      <div>
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ ...msgLabel, marginBottom: 0 }}>{t("messaging.messageComposer.buttons", "Buttons")}<span style={{ color: T.soft, fontWeight: 400 }}>{t("messaging.messageComposer.upTo3", "· up to 3")}</span></div>
          <span style={{ flex: 1 }} />
          {/* ui-raw: text action tinted with the WhatsApp brand hue (WA_HUE) — Btn's link variant inks from the brand token and takes no hue */}
          {wa.buttons.length < 3 ? <button type="button" onClick={addBtn} style={{ border: "none", background: "transparent", color: WA_HUE, fontWeight: 700, fontSize: 12.5, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}><Icon.plus size={13} /> {t("common.actions.add", "Add")}</button> : null}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
          {wa.buttons.length === 0 ? <div style={{ fontSize: 12, color: T.soft }}>{t("messaging.messageComposer.noButtonsAddQuickRepliesAUrlOrACallButton", "No buttons. Add quick replies, a URL, or a call button.")}</div> : null}
          {wa.buttons.map((b) => (
            <div key={b.id} style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{ position: "relative", width: 130, flexShrink: 0 }}>
                <select value={b.type} onChange={(e) => setBtn(b.id, { type: e.target.value as WaButtonType })} style={msgInput({ appearance: "none", WebkitAppearance: "none", paddingRight: 28, height: 36, fontSize: 13, cursor: "pointer" })}>
                  {WA_BTN_TYPES.map((type) => <option key={type.id} value={type.id}>{t(type.labelKey, type.label)}</option>)}
                </select>
                <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: T.n2 }}>{CARET}</span>
              </div>
              <input value={b.label} onChange={(e) => setBtn(b.id, { label: e.target.value })} placeholder={t("messaging.messageComposer.labelMax25", "Label (max 25)")} maxLength={25} style={msgInput({ height: 36, fontSize: 13 })} />
              {b.type !== "quick_reply" ? <input value={b.value || ""} onChange={(e) => setBtn(b.id, { value: e.target.value })} placeholder={b.type === "url" ? t("events.creationCreateEventDetails.https", "https://…") : "+1 555…"} style={msgInput({ height: 36, fontSize: 13 })} /> : null}
              <IconBtn size={32} label={t("messaging.messageComposer.removeButton", "Remove button")} onClick={() => rmBtn(b.id)} style={{ flexShrink: 0 }}><Icon.x size={13} /></IconBtn>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── WhatsApp bubble preview ─────────────────────────────────────────────── */
function WaPreview({ wa, body }: { wa: WaTemplate; body: string }) {
  const t = useT();
  const header = WA_HEADERS.find((item) => item.id === wa.headerType);
  return (
    <div style={{ background: "#ECE5DD", borderRadius: 10, padding: 14 }}>
      <div style={{ background: T.white, borderRadius: "8px 8px 8px 2px", boxShadow: "0 1px 1px rgba(0,0,0,0.12)", overflow: "hidden", maxWidth: 300 }}>
        {wa.headerType === "text" && wa.headerText ? <div style={{ padding: "10px 12px 0", fontSize: 14, fontWeight: 700, color: T.n1 }}>{wa.headerText}</div> : null}
        {wa.headerType !== "none" && wa.headerType !== "text" ? <div style={{ height: 90, background: "#D9E7DC", display: "flex", alignItems: "center", justifyContent: "center", color: "#128C3E", fontSize: 12, fontWeight: 600 }}>{header ? t(header.labelKey, header.label) : ""} {t("messaging.messageComposer.media", "media")}</div> : null}
        <div style={{ padding: "10px 12px", fontSize: 13, color: T.n1, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{body || <span style={{ color: T.soft }}>{t("messaging.messageComposer.yourTemplateBodyWillAppearHere", "Your template body will appear here.")}</span>}</div>
        {wa.footer ? <div style={{ padding: "0 12px 8px", fontSize: 11, color: T.soft }}>{wa.footer}</div> : null}
        <div style={{ padding: "0 10px 6px", textAlign: "right", fontSize: 10, color: T.soft }}>12:00</div>
      </div>
      {wa.buttons.filter((b) => b.label.trim()).length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4, maxWidth: 300 }}>
          {wa.buttons.filter((b) => b.label.trim()).map((b) => (
            <div key={b.id} style={{ background: T.white, borderRadius: 8, boxShadow: "0 1px 1px rgba(0,0,0,0.12)", padding: "9px 12px", textAlign: "center", fontSize: 13, fontWeight: 600, color: "#00A5F4", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              {b.type === "url" ? <Icon.external size={13} /> : b.type === "call" ? <Icon.phone size={13} /> : <Icon.msg size={13} />}{b.label}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ConversationalEditor (Messenger / Instagram) removed 2026-07-30 — those
   channels were retired from the builder (operator; replacements come later).
   The quickReplies data field survives on MessageItem so legacy rows round-trip. */
